// src/app/core/enums/dashboard-cards.enum.ts

/**
 * Centralized dashboard widget registry — enum string values must match backend `widgets.widget_key`.
 */

export enum DashboardPageKey {
  DASHBOARD_OVERVIEW = 'dashboard-overview',
  MONTHLY_REPORT_1 = 'monthly-report-1',
  MONTHLY_REPORT_2 = 'monthly-report-2',
  SOCIAL_PLATFORM = 'social-platform',
  SEO_STATS = 'seo-stats',
  SEO_OVERVIEW = 'seo-overview',
  CAMPAIGN_ADS_STATS = 'campaign-ads-stats',
  INDUSTRY_CATEGORIZATION = 'industry-categorization',
  TRAINING_DASHBOARD = 'training-dashboard',
  PRACTINA_DASHBOARD = 'practina-dashboard',
  LENDEE_ANALYTICS = 'lendee-analytics-dashboard',
  LENDEE_DASHBOARD = 'lendee-dashboard',
  COOL_CREDIT_DASHBOARD = 'cool-credit-dashboard',
  GROWTH_ACTIVATION_REVENUE = 'growth-activation-and-revenue',
  BUSINESS_PERFORMANCE_REPORT = 'business-performance-report',
  CONTRACTS_REPORT = 'contracts-report',
  DAILY_CRON_PAYMENTS = 'daily-cron-payments',
  OVERDUE_CRON_PAYMENTS = 'overdue-cron-payments',
  ARRANGEMENT_CRON_PAYMENTS = 'arrangement-cron-payments',
  ARRANGEMENT_CRON_OVERVIEW = 'arrangement-cron-overview',
  ARRANGEMENT_CRON_TRENDS = 'arrangement-cron-trends',
  ARRANGEMENT_CRON_ADMIN_PERFORMANCE = 'arrangement-cron-admin-performance',
}

/** Optional overrides persisted in custom_config JSON */
export interface DashboardWidgetCustomConfig {
  title?: string;
  theme?: string;
  accentColor?: string;
}

export interface DashboardCardConfig<TCardId extends string> {
  id: TCardId;
  title: string;
  defaultOrder: number;
  defaultVisible: boolean;
}

/**
 * Resolved layout row used by the UI (merge of API + defaults).
 * `widgetId` is catalog `widgets.id` (required for PUT order); 0 if unknown until catalog sync.
 * `placementId` is `user_dashboard_widgets.id` when returned by GET (for PATCH visibility).
 */
export interface DashboardCardLayoutItem<TCardId extends string = MonthlyReport1CardId> {
  id: TCardId;
  order: number;
  visible: boolean;
  widgetId: number;
  placementId?: number;
  colSpan?: number | null;
  rowSpan?: number | null;
  sourcePageId?: number | null;
  sourcePageName?: string | null;
  customConfig?: DashboardWidgetCustomConfig | null;
  minCols?: number | null;
  defaultCols?: number | null;
  /** If present and non-empty, user role must match one of these role ids to see the widget */
  allowedRoleIds?: number[];
}

export type SectionDef = { id: string; title: string };

// ─── Monthly Report 1 ─────────────────────────────────────────────────────────

export enum MonthlyReport1CardId {
  ACTIVE_BUSINESSES_MOM = 'kpi_active_businesses_mom',
  CONTRACT_TYPES_OVERVIEW = 'kpi_contract_types_overview',
  TOP_CATEGORIES_BY_CONTRACTS = 'chart_top_categories_contracts',
  ENROLLED_BUSINESSES = 'table_enrolled_businesses',
  CONTRACTS_CREATED = 'table_contracts_created',
  OVERALL_CONTRACT_TYPE_COUNT = 'chart_overall_contract_type_count',
  TOP_BUSINESSES_BY_CONTRACTS = 'chart_top_businesses_contracts',
  BUSINESS_CONTRACT_TABLE = 'table_business_contract_detail',
}

export const MONTHLY_REPORT_1_CARD_CONFIG: DashboardCardConfig<MonthlyReport1CardId>[] = [
  { id: MonthlyReport1CardId.ACTIVE_BUSINESSES_MOM, title: 'MoM Active Businesses', defaultOrder: 0, defaultVisible: true },
  { id: MonthlyReport1CardId.CONTRACT_TYPES_OVERVIEW, title: 'Contract Types Overview', defaultOrder: 1, defaultVisible: true },
  { id: MonthlyReport1CardId.TOP_CATEGORIES_BY_CONTRACTS, title: 'Top Categories by Contracts', defaultOrder: 2, defaultVisible: true },
  { id: MonthlyReport1CardId.ENROLLED_BUSINESSES, title: 'Enrolled Businesses', defaultOrder: 3, defaultVisible: true },
  { id: MonthlyReport1CardId.CONTRACTS_CREATED, title: 'Contracts Created', defaultOrder: 4, defaultVisible: true },
  { id: MonthlyReport1CardId.OVERALL_CONTRACT_TYPE_COUNT, title: 'Overall Contract Type Count', defaultOrder: 5, defaultVisible: true },
  { id: MonthlyReport1CardId.TOP_BUSINESSES_BY_CONTRACTS, title: 'Top 10 Businesses by Contracts', defaultOrder: 6, defaultVisible: true },
  { id: MonthlyReport1CardId.BUSINESS_CONTRACT_TABLE, title: 'Businesses wise Contract / Overall Detail', defaultOrder: 7, defaultVisible: true },
];

const MONTHLY_REPORT_1_KEYS = new Set<string>(MONTHLY_REPORT_1_CARD_CONFIG.map((c) => c.id));

export function isMonthlyReport1CardId(key: string): key is MonthlyReport1CardId {
  return MONTHLY_REPORT_1_KEYS.has(key);
}

/**
 * Maps catalog `widget_key` from the backend (e.g. mr1_*) to the template card id used in MR1 `@switch`.
 */
export const MR1_BACKEND_WIDGET_KEY_TO_CARD: Readonly<Record<string, MonthlyReport1CardId>> = {
  mr1_mom_active_businesses: MonthlyReport1CardId.ACTIVE_BUSINESSES_MOM,
  mr1_contract_types_overview: MonthlyReport1CardId.CONTRACT_TYPES_OVERVIEW,
  mr1_top_categories_contracts: MonthlyReport1CardId.TOP_CATEGORIES_BY_CONTRACTS,
  mr1_enrolled_businesses: MonthlyReport1CardId.ENROLLED_BUSINESSES,
  mr1_contracts_created: MonthlyReport1CardId.CONTRACTS_CREATED,
  mr1_overall_contract_type_count: MonthlyReport1CardId.OVERALL_CONTRACT_TYPE_COUNT,
  mr1_top_10_businesses: MonthlyReport1CardId.TOP_BUSINESSES_BY_CONTRACTS,
  mr1_overall_business_detail: MonthlyReport1CardId.BUSINESS_CONTRACT_TABLE,
};

/**
 * Resolves a catalog widget_key to the MR1 card id used by the Angular template.
 *
 * @param widgetKey Value from GET /dashboards widgets[].widget_key.
 * @returns Template card id when known; otherwise null.
 */
export function resolveMonthlyReport1CardFromWidgetKey(widgetKey: string): MonthlyReport1CardId | null {
  if (isMonthlyReport1CardId(widgetKey)) {
    return widgetKey;
  }
  const direct = MR1_BACKEND_WIDGET_KEY_TO_CARD[widgetKey];
  if (direct) {
    return direct;
  }
  return MR1_BACKEND_WIDGET_KEY_TO_CARD[widgetKey.toLowerCase()] ?? null;
}


// ─── Monthly Report 2 ─────────────────────────────────────────────────────────

// Values MUST stay canonical (`mr2_*`, no product prefix). The backend catalog
// ships every Monthly Report 2 widget with a product prefix on non-Denefits
// products:
//   Denefits        mr2_stats_overview          mr2_mom_activity          …
//   Credee          cr_mr2_stats_overview       cr_mr2_mom_activity       …
//   Finance Mutual  fm_mr2_stats_overview       fm_mr2_mom_activity       …
//   Recuvery        re_mr2_stats_overview       re_mr2_mom_activity       …
//
// The Denefits keys ship WITHOUT a prefix, so the exact-match arm of the
// `CustomDashboardPinService` resolver handles them; the suffix arm
// (`widget_key.endsWith('_' + sectionId)`) covers every other product.
export enum MonthlyReport2SectionId {
  STATS_OVERVIEW       = 'mr2_stats_overview',
  MOM_ACTIVITY         = 'mr2_mom_activity',
  TRAINING_STATUS      = 'mr2_training_status',
  MONTHLY_ANALYSIS     = 'mr2_monthly_analysis',
  ENROLLED_RUNNING     = 'mr2_enrolled_running',
  CONTRACTS_RUNNING    = 'mr2_contracts_running',
}

export const MonthlyReport2SectionDef: SectionDef[] = [
  { id: MonthlyReport2SectionId.STATS_OVERVIEW,    title: 'Stats Overview' },
  { id: MonthlyReport2SectionId.MOM_ACTIVITY,      title: 'MoM Activity Chart' },
  { id: MonthlyReport2SectionId.TRAINING_STATUS,   title: 'Training Status' },
  { id: MonthlyReport2SectionId.MONTHLY_ANALYSIS,  title: 'Monthly Activity Analysis' },
  { id: MonthlyReport2SectionId.ENROLLED_RUNNING,  title: 'Businesses Enrolled | Running Total' },
  { id: MonthlyReport2SectionId.CONTRACTS_RUNNING, title: 'Contracts Created | Running Total' },
];

export const MONTHLY_REPORT_2_SECTIONS: SectionDef[] = [
  { id: MonthlyReport2SectionId.STATS_OVERVIEW,    title: 'Stats Overview' },
  { id: MonthlyReport2SectionId.MOM_ACTIVITY,      title: 'MoM Activity Chart' },
  { id: MonthlyReport2SectionId.TRAINING_STATUS,   title: 'Training Status' },
  { id: MonthlyReport2SectionId.MONTHLY_ANALYSIS,  title: 'Monthly Activity Analysis' },
  { id: MonthlyReport2SectionId.ENROLLED_RUNNING,  title: 'Businesses Enrolled | Running Total' },
  { id: MonthlyReport2SectionId.CONTRACTS_RUNNING, title: 'Contracts Created | Running Total' },
];

// ─── Social Platform ───────────────────────────────────────────────────────────

// Values MUST stay canonical (no product prefix). The backend catalog ships
// every Social Media Performance widget with a product-prefixed key — one row
// per product:
//   Denefits        de_sp_overall_stats   de_sp_platform_performance   de_sp_views_trend   de_sp_overal_analytics   de_sp_watch_time   de_sp_follow_by_platform
//   Cool Credit     co_sp_overall_stats   co_sp_platform_performance   co_sp_views_trend
//   Credee          cr_sp_overall_stats   cr_sp_platform_performance   cr_sp_views_trend
//   Finance Mutual  fm_sp_overall_stats   fm_sp_platform_performance   fm_sp_views_trend
//   Lendee          le_sp_overall_stats   le_sp_platform_performance   le_sp_views_trend
//   Practina        pr_sp_overall_stats   pr_sp_platform_performance   pr_sp_views_trend
//   Recuvery        re_sp_overall_stats   re_sp_platform_performance   re_sp_views_trend
//
// `CustomDashboardPinService.resolveCatalogWidgetId` matches
//   `widget_key === sectionId` OR `widget_key.endsWith('_' + sectionId)`
// so the canonical `sp_*` enum value below resolves to the correct catalog
// widget for whichever product is active — no per-product branching needed
// on this page.
//
// ⚠ OVERALL_ANALYTICS — backend widget_key is misspelled as `sp_overal_analytics`
//   (one `l`, not two). The frontend enum value MUST mirror that spelling exactly
//   for the suffix matcher (`endsWith('_sp_overal_analytics')`) to resolve the
//   numeric `widgets.id`. Currently only Denefits exposes this widget
//   (`de_sp_overal_analytics`); other products will start matching automatically
//   once backend adds their per-product rows (e.g. `co_sp_overal_analytics`).
//   TODO(backend): rename the column to `sp_overall_analytics` (two `l`s) — then
//   update this enum value to match and remove this note.
// ⚠ WATCH_TIME / FOLLOW_BY_PLATFORM — split-out standalone variants of the two
//   sub-charts inside OVERALL_ANALYTICS (Watch Time Contribution, Followers by
//   Platform). Currently only Denefits exposes them (`de_sp_watch_time`,
//   `de_sp_follow_by_platform`); other products will start resolving via the
//   suffix matcher once backend adds their per-product rows.
// ⚠ AUDIT_LOG — purely frontend-only today; no `sp_audit_log` exists in the
//   catalog for any product, so pinning will silently warn and skip until
//   backend adds it.
export enum SocialPlatformSectionId {
  OVERALL_STATS        = 'sp_overall_stats',
  PLATFORM_PERFORMANCE = 'sp_platform_performance',
  VIEWS_TREND          = 'sp_views_trend',
  // Intentionally misspelled to mirror backend `*_sp_overal_analytics` rows.
  // See block comment above for the rename plan.
  OVERALL_ANALYTICS    = 'sp_overal_analytics',
  WATCH_TIME           = 'sp_watch_time',
  FOLLOW_BY_PLATFORM   = 'sp_follow_by_platform',
  AUDIT_LOG            = 'sp_audit_log',
}

export const SocialPlatformSectionDef: SectionDef[] = [
  { id: SocialPlatformSectionId.OVERALL_STATS,        title: 'Overall Stats Cards' },
  { id: SocialPlatformSectionId.PLATFORM_PERFORMANCE, title: 'Platform Performance Details' },
  { id: SocialPlatformSectionId.VIEWS_TREND,          title: 'Views Trend Across Platforms' },
  { id: SocialPlatformSectionId.OVERALL_ANALYTICS,    title: 'Overall Analytics' },
  { id: SocialPlatformSectionId.WATCH_TIME,           title: 'Watch Time' },
  { id: SocialPlatformSectionId.FOLLOW_BY_PLATFORM,   title: 'Follow by Platform' },
  { id: SocialPlatformSectionId.AUDIT_LOG,            title: 'Audit Log' },
];

export const SOCIAL_PLATFORM_SECTIONS: SectionDef[] = [
  { id: SocialPlatformSectionId.OVERALL_STATS,        title: 'Overall Stats Cards' },
  { id: SocialPlatformSectionId.PLATFORM_PERFORMANCE, title: 'Platform Performance Details' },
  { id: SocialPlatformSectionId.VIEWS_TREND,          title: 'Views Trend Across Platforms' },
  { id: SocialPlatformSectionId.OVERALL_ANALYTICS,    title: 'Overall Analytics' },
  { id: SocialPlatformSectionId.WATCH_TIME,           title: 'Watch Time' },
  { id: SocialPlatformSectionId.FOLLOW_BY_PLATFORM,   title: 'Follow by Platform' },
  { id: SocialPlatformSectionId.AUDIT_LOG,            title: 'Audit Log' },
];

// ─── SEO Stats ────────────────────────────────────────────────────────────────

// Values MUST stay canonical (`seo_*`, no product prefix). Backend ships
// every Seo Stats widget with a product-prefixed key per product:
//   Denefits        de_seo_stats_overview      de_seo_clicks_chart     …
//   Credee          cr_seo_stats_overview      cr_seo_clicks_chart     …
//   Finance Mutual  fm_seo_stats_overview      fm_seo_clicks_chart     …
//   Recuvery        re_seo_stats_overview      re_seo_clicks_chart     …
//
// The suffix matcher (`widget_key.endsWith('_' + sectionId)`) resolves
// the right per-product `widgets.id` regardless of which product is active.
//
// `AUDIT_LOG` has no backend counterpart today — frontend-only section,
// safe to leave; pin attempt will just warn and skip.
export enum SeoStatsSectionId {
  STATS_OVERVIEW      = 'seo_stats_overview',
  CLICKS_CHART        = 'seo_clicks_chart',
  KEYWORD_RANKING     = 'seo_keyword_ranking',
  AI_OVERVIEW         = 'seo_ai_overview',
  COUNTRY_PERFORMANCE = 'seo_country_performance',
  TOP_CITIES          = 'seo_top_cities',
  REVIEWS_RATINGS     = 'seo_reviews_ratings',
  LATEST_REVIEWS      = 'seo_latest_reviews',
  AUDIT_LOG           = 'seo_audit_log',
}

export const SeoStatsSectionDef: SectionDef[] = [
  { id: SeoStatsSectionId.STATS_OVERVIEW,      title: 'Stats Cards Overview' },
  { id: SeoStatsSectionId.CLICKS_CHART,        title: 'Clicks over Time' },
  { id: SeoStatsSectionId.KEYWORD_RANKING,     title: 'Top Keyword Ranking' },
  { id: SeoStatsSectionId.AI_OVERVIEW,         title: 'AI Overview Keyword' },
  { id: SeoStatsSectionId.COUNTRY_PERFORMANCE, title: 'Performance in Top Countries' },
  { id: SeoStatsSectionId.TOP_CITIES,          title: 'Top Cities performance' },
  { id: SeoStatsSectionId.REVIEWS_RATINGS,     title: 'Reviews and Ratings Count' },
  { id: SeoStatsSectionId.LATEST_REVIEWS,      title: 'Latest Reviews Feed' },
  { id: SeoStatsSectionId.AUDIT_LOG,           title: 'Audit Logs' },
];

// ─── SEO Overview ─────────────────────────────────────────────────────────────

// Backend page name is "Ads Overview" (slug `ads-overview`), and every widget
// on it ships with an `ao_` (= ads-overview) prefix per product:
//   Denefits        de_ao_active_campaigns   de_ao_platforms   de_ao_device   …
//   Cool Credit     co_ao_active_campaigns   co_ao_platforms   co_ao_device   …
//   Credee          cr_ao_active_campaigns   cr_ao_platforms   cr_ao_device   …
//   Finance Mutual  fm_ao_active_campaigns   …
//   Lendee          le_ao_active_campaigns   …
//   Practina        pr_ao_active_campaigns   …
//   Recuvery        re_ao_active_campaigns   …
//
// Values MUST stay canonical (`ao_*`, no product prefix) so the
// `CustomDashboardPinService` suffix matcher
// (`widget_key.endsWith('_' + sectionId)`) resolves the correct
// per-product `widgets.id` for whichever product is active.
//
// `STATS_OVERVIEW` has no backend counterpart today (backend starts at
// `default_position: 1` on every product's Ads Overview page) — it's a
// frontend-only section, kept here so the layout editor still lists it.
// We use the same `ao_` prefix for naming consistency; the suffix matcher
// will simply not find a backend widget, log a clear `[CustomDashboardPin]`
// warning, and skip the pin call if anyone tries to pin it.
export enum SeoOverviewSectionId {
  STATS_OVERVIEW    = 'ao_stats_overview',
  ACTIVE_CAMPAIGNS  = 'ao_active_campaigns',
  PLATFORMS         = 'ao_platforms',
  DEVICE            = 'ao_device',
  GENDER            = 'ao_gender',
  TOP_CITIES        = 'ao_top_cities',
  AGE               = 'ao_age',
}

export const SeoOverviewSectionDef: SectionDef[] = [
  { id: SeoOverviewSectionId.STATS_OVERVIEW,   title: 'Stats Cards Overview' },
  { id: SeoOverviewSectionId.ACTIVE_CAMPAIGNS, title: 'Active Campaigns' },
  { id: SeoOverviewSectionId.PLATFORMS,        title: 'Platforms' },
  { id: SeoOverviewSectionId.DEVICE,           title: 'Device' },
  { id: SeoOverviewSectionId.GENDER,           title: 'Gender' },
  { id: SeoOverviewSectionId.TOP_CITIES,       title: 'Top Cities' },
  { id: SeoOverviewSectionId.AGE,              title: 'Age' },
];

export const SEO_OVERVIEW_CARD_CONFIG: DashboardCardConfig<SeoOverviewSectionId>[] = [
  { id: SeoOverviewSectionId.STATS_OVERVIEW,   title: 'Stats Cards Overview', defaultOrder: 0, defaultVisible: true },
  { id: SeoOverviewSectionId.ACTIVE_CAMPAIGNS, title: 'Active Campaigns',     defaultOrder: 1, defaultVisible: true },
  { id: SeoOverviewSectionId.PLATFORMS,        title: 'Platforms',            defaultOrder: 2, defaultVisible: true },
  { id: SeoOverviewSectionId.DEVICE,           title: 'Device',               defaultOrder: 3, defaultVisible: true },
  { id: SeoOverviewSectionId.GENDER,           title: 'Gender',               defaultOrder: 4, defaultVisible: true },
  { id: SeoOverviewSectionId.TOP_CITIES,       title: 'Top Cities',           defaultOrder: 5, defaultVisible: true },
  { id: SeoOverviewSectionId.AGE,              title: 'Age',                  defaultOrder: 6, defaultVisible: true },
];

const SEO_OVERVIEW_KEYS = new Set<string>(SEO_OVERVIEW_CARD_CONFIG.map((c) => c.id));
export function isSeoOverviewCardId(key: string): key is SeoOverviewSectionId {
  return SEO_OVERVIEW_KEYS.has(key);
}

// ─── Campaign Ads Stats ───────────────────────────────────────────────────────

// Values MUST stay canonical (`ca_*`, no product prefix). Backend ships
// every Campaign Metrics widget with a product-prefixed key:
//   Denefits        de_ca_stats_overview      de_ca_mom_comparison      …
//   Credee          cr_ca_stats_overview      cr_ca_mom_comparison      …
//   Finance Mutual  fm_ca_stats_overview      fm_ca_mom_comparison      …
//   Recuvery        re_ca_stats_overview      re_ca_mom_comparison      …
//
// Suffix matcher (`widget_key.endsWith('_' + sectionId)`) resolves the
// right per-product `widgets.id`.
export enum CampaignAdsStatsSectionId {
  STATS_OVERVIEW    = 'ca_stats_overview',
  MOM_COMPARISON    = 'ca_mom_comparison',
  MONTHLY_ENQUIRIES = 'ca_monthly_enquiries',
  AUDIT_LOG         = 'ca_audit_log',
}

export const CampaignAdsStatsSectionDef: SectionDef[] = [
  { id: CampaignAdsStatsSectionId.STATS_OVERVIEW,    title: 'Stats Overview' },
  { id: CampaignAdsStatsSectionId.MOM_COMPARISON,    title: 'MoM Comparison' },
  { id: CampaignAdsStatsSectionId.MONTHLY_ENQUIRIES, title: 'Monthly Enquiries and Conversion' },
  { id: CampaignAdsStatsSectionId.AUDIT_LOG,         title: 'Audit Logs' },
];

// ─── Industry Categorization ──────────────────────────────────────────────────

// Values MUST stay canonical (`ic_*`, no product prefix). Backend ships
// every Industry Categorization widget with a product-prefixed key:
//   Denefits        de_ic_industry_categorization     de_ic_city_categorization
//   Credee          cr_ic_industry_categorization     cr_ic_city_categorization
//   Finance Mutual  fm_ic_industry_categorization     fm_ic_city_categorization
//   Recuvery        re_ic_industry_categorization     re_ic_city_categorization
//
// Suffix matcher resolves the right per-product `widgets.id`.
export enum IndustryCategorizationSectionId {
  INDUSTRY_CATEGORIZATION = 'ic_industry_categorization',
  CITY_CATEGORIZATION     = 'ic_city_categorization',
}

export const IndustryCategorizationSectionDef: SectionDef[] = [
  { id: IndustryCategorizationSectionId.INDUSTRY_CATEGORIZATION, title: 'Industry Wise Categorization' },
  { id: IndustryCategorizationSectionId.CITY_CATEGORIZATION,     title: 'City Wise Categorization' },
];

// ─── Training Dashboard ───────────────────────────────────────────────────────

// Values MUST stay canonical (`td_*`, no product prefix). Backend currently
// only exposes Training Dashboard on Denefits (page 35):
//   Denefits   de_td_stats_overview   de_td_performance_overview   …
//
// When other products start exposing this page they'll use their own prefix
// (e.g. `cr_td_*`, `re_td_*`) — the suffix matcher
// (`widget_key.endsWith('_' + sectionId)`) will pick them up automatically
// without any frontend change.
export enum TrainingDashboardSectionId {
  STATS_OVERVIEW                  = 'td_stats_overview',
  PERFORMANCE_OVERVIEW           = 'td_performance_overview',
  SOURCE_CHANNEL_PERFORMANCE     = 'td_source_channel_performance',
  COMPLETION_BY_TYPE             = 'td_completion_by_type',
  CANCELLATION_LEAD_TIME         = 'td_cancellation_lead_time',
  STATUS_DISTRIBUTION            = 'td_status_distribution',
  ATTEMPTS_BEFORE_COMPLETION     = 'td_attempts_before_completion',
  UNREGISTERED_BOOKING_CONVERSION = 'td_unregistered_booking_conversion',
  POST_REGISTRATION_JOURNEY      = 'td_post_registration_journey',
  TIME_TO_FIRST_CONTRACT         = 'td_time_to_first_contract',
  TOP_ADMINS                     = 'td_top_admins',
  ADMIN_CONVERSION_TABLE         = 'td_admin_conversion_table',
}

export const TrainingDashboardSectionDef: SectionDef[] = [
  { id: TrainingDashboardSectionId.STATS_OVERVIEW,                  title: 'Stats Overview' },
  { id: TrainingDashboardSectionId.PERFORMANCE_OVERVIEW,           title: 'Monthly Training Performance' },
  { id: TrainingDashboardSectionId.SOURCE_CHANNEL_PERFORMANCE,     title: 'Performance by Source Channel' },
  { id: TrainingDashboardSectionId.COMPLETION_BY_TYPE,             title: 'Completion Rate by Type' },
  { id: TrainingDashboardSectionId.CANCELLATION_LEAD_TIME,         title: 'Cancellation Rate by Lead Time' },
  { id: TrainingDashboardSectionId.STATUS_DISTRIBUTION,            title: 'Session Status Distribution' },
  { id: TrainingDashboardSectionId.ATTEMPTS_BEFORE_COMPLETION,     title: 'Attempts Before Completion' },
  { id: TrainingDashboardSectionId.UNREGISTERED_BOOKING_CONVERSION, title: 'Unregistered Booking Conversion' },
  { id: TrainingDashboardSectionId.POST_REGISTRATION_JOURNEY,      title: 'Post-Training Registration Journey' },
  { id: TrainingDashboardSectionId.TIME_TO_FIRST_CONTRACT,         title: 'Time to First Contract Distribution' },
  { id: TrainingDashboardSectionId.TOP_ADMINS,                     title: 'Top Admins by Sessions Managed' },
  { id: TrainingDashboardSectionId.ADMIN_CONVERSION_TABLE,         title: 'Full Admin Conversion Table' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Engine-ready DashboardCardConfig arrays + isCardId validators
//
// Each page that wants to adopt DashboardPageEngine (API-backed widget layout)
// only needs to supply:
//   protected readonly dashboardEngineConfig = {
//     pageId: <backend pages.id>,
//     cardConfig: <PAGE>_CARD_CONFIG,
//     isCardId: is<Page>CardId,
//   };
//
// Until a page migrates, it continues using DashboardSectionLayoutService
// (localStorage section visibility) unchanged.
// ─────────────────────────────────────────────────────────────────────────────

// ─── Monthly Report 2 ─────────────────────────────────────────────────────────

export const MONTHLY_REPORT_2_CARD_CONFIG: DashboardCardConfig<MonthlyReport2SectionId>[] = [
  { id: MonthlyReport2SectionId.STATS_OVERVIEW,    title: 'Stats Overview',                          defaultOrder: 0, defaultVisible: true },
  { id: MonthlyReport2SectionId.MOM_ACTIVITY,      title: 'MoM Activity Chart',                      defaultOrder: 1, defaultVisible: true },
  { id: MonthlyReport2SectionId.TRAINING_STATUS,   title: 'Training Status',                         defaultOrder: 2, defaultVisible: true },
  { id: MonthlyReport2SectionId.MONTHLY_ANALYSIS,  title: 'Monthly Activity Analysis',               defaultOrder: 3, defaultVisible: true },
  { id: MonthlyReport2SectionId.ENROLLED_RUNNING,  title: 'Businesses Enrolled | Running Total',     defaultOrder: 4, defaultVisible: true },
  { id: MonthlyReport2SectionId.CONTRACTS_RUNNING, title: 'Contracts Created | Running Total',       defaultOrder: 5, defaultVisible: true },
];

const MONTHLY_REPORT_2_KEYS = new Set<string>(MONTHLY_REPORT_2_CARD_CONFIG.map((c) => c.id));
export function isMonthlyReport2CardId(key: string): key is MonthlyReport2SectionId {
  return MONTHLY_REPORT_2_KEYS.has(key);
}

// ─── Social Platform ──────────────────────────────────────────────────────────

export const SOCIAL_PLATFORM_CARD_CONFIG: DashboardCardConfig<SocialPlatformSectionId>[] = [
  { id: SocialPlatformSectionId.OVERALL_STATS,        title: 'Overall Stats Cards',              defaultOrder: 0, defaultVisible: true },
  { id: SocialPlatformSectionId.PLATFORM_PERFORMANCE, title: 'Platform Performance Details',    defaultOrder: 1, defaultVisible: true },
  { id: SocialPlatformSectionId.VIEWS_TREND,          title: 'Views Trend Across Platforms',    defaultOrder: 2, defaultVisible: true },
  { id: SocialPlatformSectionId.OVERALL_ANALYTICS,    title: 'Overall Analytics',               defaultOrder: 3, defaultVisible: true },
  { id: SocialPlatformSectionId.AUDIT_LOG,            title: 'Audit Log',                       defaultOrder: 4, defaultVisible: true },
];

const SOCIAL_PLATFORM_KEYS = new Set<string>(SOCIAL_PLATFORM_CARD_CONFIG.map((c) => c.id));
export function isSocialPlatformCardId(key: string): key is SocialPlatformSectionId {
  return SOCIAL_PLATFORM_KEYS.has(key);
}

// ─── SEO Stats ────────────────────────────────────────────────────────────────

export const SEO_STATS_CARD_CONFIG: DashboardCardConfig<SeoStatsSectionId>[] = [
  { id: SeoStatsSectionId.STATS_OVERVIEW,      title: 'Stats Cards Overview',             defaultOrder: 0, defaultVisible: true },
  { id: SeoStatsSectionId.CLICKS_CHART,        title: 'Clicks over Time',                 defaultOrder: 1, defaultVisible: true },
  { id: SeoStatsSectionId.KEYWORD_RANKING,     title: 'Top Keyword Ranking',              defaultOrder: 2, defaultVisible: true },
  { id: SeoStatsSectionId.AI_OVERVIEW,         title: 'AI Overview Keyword',              defaultOrder: 3, defaultVisible: true },
  { id: SeoStatsSectionId.COUNTRY_PERFORMANCE, title: 'Performance in Top Countries',     defaultOrder: 4, defaultVisible: true },
  { id: SeoStatsSectionId.TOP_CITIES,          title: 'Top Cities performance',           defaultOrder: 5, defaultVisible: true },
  { id: SeoStatsSectionId.REVIEWS_RATINGS,     title: 'Reviews and Ratings Count',       defaultOrder: 6, defaultVisible: true },
  { id: SeoStatsSectionId.LATEST_REVIEWS,      title: 'Latest Reviews Feed',              defaultOrder: 7, defaultVisible: true },
  { id: SeoStatsSectionId.AUDIT_LOG,           title: 'Audit Logs',                       defaultOrder: 8, defaultVisible: true },
];

const SEO_STATS_KEYS = new Set<string>(SEO_STATS_CARD_CONFIG.map((c) => c.id));
export function isSeoStatsCardId(key: string): key is SeoStatsSectionId {
  return SEO_STATS_KEYS.has(key);
}

// ─── Campaign Ads Stats ───────────────────────────────────────────────────────

export const CAMPAIGN_ADS_STATS_CARD_CONFIG: DashboardCardConfig<CampaignAdsStatsSectionId>[] = [
  { id: CampaignAdsStatsSectionId.STATS_OVERVIEW,    title: 'Stats Overview',                         defaultOrder: 0, defaultVisible: true },
  { id: CampaignAdsStatsSectionId.MOM_COMPARISON,    title: 'MoM Comparison',                         defaultOrder: 1, defaultVisible: true },
  { id: CampaignAdsStatsSectionId.MONTHLY_ENQUIRIES, title: 'Monthly Enquiries and Conversion',       defaultOrder: 2, defaultVisible: true },
  { id: CampaignAdsStatsSectionId.AUDIT_LOG,         title: 'Audit Logs',                             defaultOrder: 3, defaultVisible: true },
];

const CAMPAIGN_ADS_STATS_KEYS = new Set<string>(CAMPAIGN_ADS_STATS_CARD_CONFIG.map((c) => c.id));
export function isCampaignAdsStatsCardId(key: string): key is CampaignAdsStatsSectionId {
  return CAMPAIGN_ADS_STATS_KEYS.has(key);
}

// ─── Industry Categorization ──────────────────────────────────────────────────

export const INDUSTRY_CATEGORIZATION_CARD_CONFIG: DashboardCardConfig<IndustryCategorizationSectionId>[] = [
  { id: IndustryCategorizationSectionId.INDUSTRY_CATEGORIZATION, title: 'Industry Wise Categorization', defaultOrder: 0, defaultVisible: true },
  { id: IndustryCategorizationSectionId.CITY_CATEGORIZATION,     title: 'City Wise Categorization',     defaultOrder: 1, defaultVisible: true },
];

const INDUSTRY_CATEGORIZATION_KEYS = new Set<string>(INDUSTRY_CATEGORIZATION_CARD_CONFIG.map((c) => c.id));
export function isIndustryCategorizationCardId(key: string): key is IndustryCategorizationSectionId {
  return INDUSTRY_CATEGORIZATION_KEYS.has(key);
}

// ─── Training Dashboard ───────────────────────────────────────────────────────

export const TRAINING_DASHBOARD_CARD_CONFIG: DashboardCardConfig<TrainingDashboardSectionId>[] = [
  { id: TrainingDashboardSectionId.STATS_OVERVIEW,                   title: 'Stats Overview',                         defaultOrder: 0,  defaultVisible: true },
  { id: TrainingDashboardSectionId.PERFORMANCE_OVERVIEW,            title: 'Monthly Training Performance',           defaultOrder: 1,  defaultVisible: true },
  { id: TrainingDashboardSectionId.SOURCE_CHANNEL_PERFORMANCE,      title: 'Performance by Source Channel',          defaultOrder: 2,  defaultVisible: true },
  { id: TrainingDashboardSectionId.COMPLETION_BY_TYPE,              title: 'Completion Rate by Type',                defaultOrder: 3,  defaultVisible: true },
  { id: TrainingDashboardSectionId.CANCELLATION_LEAD_TIME,          title: 'Cancellation Rate by Lead Time',         defaultOrder: 4,  defaultVisible: true },
  { id: TrainingDashboardSectionId.STATUS_DISTRIBUTION,             title: 'Session Status Distribution',            defaultOrder: 5,  defaultVisible: true },
  { id: TrainingDashboardSectionId.ATTEMPTS_BEFORE_COMPLETION,      title: 'Attempts Before Completion',             defaultOrder: 6,  defaultVisible: true },
  { id: TrainingDashboardSectionId.UNREGISTERED_BOOKING_CONVERSION, title: 'Unregistered Booking Conversion',        defaultOrder: 7,  defaultVisible: true },
  { id: TrainingDashboardSectionId.POST_REGISTRATION_JOURNEY,       title: 'Post-Training Registration Journey',     defaultOrder: 8,  defaultVisible: true },
  { id: TrainingDashboardSectionId.TIME_TO_FIRST_CONTRACT,          title: 'Time to First Contract Distribution',    defaultOrder: 9,  defaultVisible: true },
  { id: TrainingDashboardSectionId.TOP_ADMINS,                      title: 'Top Admins by Sessions Managed',         defaultOrder: 10, defaultVisible: true },
  { id: TrainingDashboardSectionId.ADMIN_CONVERSION_TABLE,          title: 'Full Admin Conversion Table',            defaultOrder: 11, defaultVisible: true },
];

const TRAINING_DASHBOARD_KEYS = new Set<string>(TRAINING_DASHBOARD_CARD_CONFIG.map((c) => c.id));
export function isTrainingDashboardCardId(key: string): key is TrainingDashboardSectionId {
  return TRAINING_DASHBOARD_KEYS.has(key);
}

// ─── Practina Dashboard ───────────────────────────────────────────────────────

export enum PractinaDashboardSectionId {
  ACQUISITION                 = 'pd_acquisition',
  CONVERSION                  = 'pd_conversion',
  SUBSCRIPTIONS               = 'pd_subscriptions',
  MONTHLY_SIGNUP_TREND        = 'pd_monthly_signup_trend',
  PROFILE_COMPLETION          = 'pd_profile_completion',
  SIGNUP_REVENUE_FUNNEL       = 'pd_signup_revenue_funnel',
  CONVERSION_MONETIZATION     = 'pd_conversion_monetization',
  CONVERSION_PIPELINE         = 'pd_conversion_pipeline',
  RENEWAL_TREND               = 'pd_renewal_trend',
  AI_CONTENT_TREND            = 'pd_ai_content_trend',
  AD_LEADS_CAMPAIGN           = 'pd_ad_leads_campaign',
  PLATFORM_POST_DISTRIBUTION  = 'pd_platform_post_distribution',
  AD_SPEND_CHANNEL            = 'pd_ad_spend_channel',
  DEMO_SIGNUP_TREND           = 'pd_demo_signup_trend',
  DEAL_REALIZATION            = 'pd_deal_realization',
  MARKETING_OVERVIEW          = 'pd_marketing_overview',
  EXECUTIVE_SUMMARY           = 'pd_executive_summary',
}

export const PractinaDashboardSectionDef: SectionDef[] = [
  { id: PractinaDashboardSectionId.ACQUISITION,                title: 'Acquisition' },
  { id: PractinaDashboardSectionId.CONVERSION,                 title: 'Conversion' },
  { id: PractinaDashboardSectionId.SUBSCRIPTIONS,              title: 'Subscriptions' },
  { id: PractinaDashboardSectionId.MONTHLY_SIGNUP_TREND,       title: 'Monthly Signup Trend (Total vs Social)' },
  { id: PractinaDashboardSectionId.PROFILE_COMPLETION,         title: 'Profile vs Onboarding Completion' },
  { id: PractinaDashboardSectionId.SIGNUP_REVENUE_FUNNEL,      title: 'Signup to Revenue Journey' },
  { id: PractinaDashboardSectionId.CONVERSION_MONETIZATION,    title: 'User Conversion & Monetization Trend' },
  { id: PractinaDashboardSectionId.CONVERSION_PIPELINE,        title: 'Conversion Pipeline' },
  { id: PractinaDashboardSectionId.RENEWAL_TREND,              title: 'Renewal & Payment Stability' },
  { id: PractinaDashboardSectionId.AI_CONTENT_TREND,          title: 'AI Content Contribution Trend' },
  { id: PractinaDashboardSectionId.AD_LEADS_CAMPAIGN,          title: 'Ad Leads & Campaign Activity' },
  { id: PractinaDashboardSectionId.PLATFORM_POST_DISTRIBUTION, title: 'User Content Distribution' },
  { id: PractinaDashboardSectionId.AD_SPEND_CHANNEL,           title: 'Ad Spend by Channel' },
  { id: PractinaDashboardSectionId.DEMO_SIGNUP_TREND,          title: 'Demo-to-Signup Conversion Trend' },
  { id: PractinaDashboardSectionId.DEAL_REALIZATION,           title: 'Deal Realization Performance' },
  { id: PractinaDashboardSectionId.MARKETING_OVERVIEW,         title: 'Marketing Performance Overview' },
  { id: PractinaDashboardSectionId.EXECUTIVE_SUMMARY,          title: 'Executive Performance Summary' },
];

export const PRACTINA_DASHBOARD_CARD_CONFIG: DashboardCardConfig<PractinaDashboardSectionId>[] = [
  { id: PractinaDashboardSectionId.ACQUISITION,                title: 'Acquisition',                              defaultOrder: 0,  defaultVisible: true },
  { id: PractinaDashboardSectionId.CONVERSION,                 title: 'Conversion',                               defaultOrder: 1,  defaultVisible: true },
  { id: PractinaDashboardSectionId.SUBSCRIPTIONS,              title: 'Subscriptions',                            defaultOrder: 2,  defaultVisible: true },
  { id: PractinaDashboardSectionId.MONTHLY_SIGNUP_TREND,       title: 'Monthly Signup Trend (Total vs Social)',   defaultOrder: 3,  defaultVisible: true },
  { id: PractinaDashboardSectionId.PROFILE_COMPLETION,         title: 'Profile vs Onboarding Completion',         defaultOrder: 4,  defaultVisible: true },
  { id: PractinaDashboardSectionId.SIGNUP_REVENUE_FUNNEL,      title: 'Signup to Revenue Journey',                defaultOrder: 5,  defaultVisible: true },
  { id: PractinaDashboardSectionId.CONVERSION_MONETIZATION,    title: 'User Conversion & Monetization Trend',   defaultOrder: 6,  defaultVisible: true },
  { id: PractinaDashboardSectionId.CONVERSION_PIPELINE,        title: 'Conversion Pipeline',                      defaultOrder: 7,  defaultVisible: true },
  { id: PractinaDashboardSectionId.RENEWAL_TREND,              title: 'Renewal & Payment Stability',              defaultOrder: 8,  defaultVisible: true },
  { id: PractinaDashboardSectionId.AI_CONTENT_TREND,          title: 'AI Content Contribution Trend',            defaultOrder: 9,  defaultVisible: true },
  { id: PractinaDashboardSectionId.AD_LEADS_CAMPAIGN,          title: 'Ad Leads & Campaign Activity',             defaultOrder: 10, defaultVisible: true },
  { id: PractinaDashboardSectionId.PLATFORM_POST_DISTRIBUTION, title: 'User Content Distribution',                defaultOrder: 11, defaultVisible: true },
  { id: PractinaDashboardSectionId.AD_SPEND_CHANNEL,           title: 'Ad Spend by Channel',                      defaultOrder: 12, defaultVisible: true },
  { id: PractinaDashboardSectionId.DEMO_SIGNUP_TREND,          title: 'Demo-to-Signup Conversion Trend',          defaultOrder: 13, defaultVisible: true },
  { id: PractinaDashboardSectionId.DEAL_REALIZATION,           title: 'Deal Realization Performance',             defaultOrder: 14, defaultVisible: true },
  { id: PractinaDashboardSectionId.MARKETING_OVERVIEW,         title: 'Marketing Performance Overview',           defaultOrder: 15, defaultVisible: true },
  { id: PractinaDashboardSectionId.EXECUTIVE_SUMMARY,          title: 'Executive Performance Summary',            defaultOrder: 16, defaultVisible: true },
];

const PRACTINA_DASHBOARD_KEYS = new Set<string>(PRACTINA_DASHBOARD_CARD_CONFIG.map((c) => c.id));
export function isPractinaDashboardCardId(key: string): key is PractinaDashboardSectionId {
  return PRACTINA_DASHBOARD_KEYS.has(key);
}

// ─── Lendee Analytics Dashboard ───────────────────────────────────────────────

export enum LendeeAnalyticsSectionId {
  METRICS_OVERVIEW           = 'ld_metrics_overview',
  KEY_INSIGHTS               = 'ld_key_insights',
  RECIPIENT_FUNNEL           = 'ld_recipient_funnel',
  REQUEST_OFFER_PERFORMANCE  = 'ld_request_offer_performance',
  SIGNUPS_TREND              = 'ld_signups_trend',
  REQUEST_STATUS             = 'ld_request_status',
  OFFER_OUTCOME              = 'ld_offer_outcome',
  OVERDUE_COLLECTIONS        = 'ld_overdue_collections',
  PARTIAL_REPAYMENTS         = 'ld_partial_repayments',
  RECEIVABLES_VS_REVENUE     = 'ld_receivables_vs_revenue',
  RECEIVABLES_RECOVERY       = 'ld_receivables_recovery',
  DECLINE_REASONS            = 'ld_decline_reasons',
}

export const LendeeAnalyticsSectionDef: SectionDef[] = [
  { id: LendeeAnalyticsSectionId.METRICS_OVERVIEW,          title: 'Metrics Overview' },
  { id: LendeeAnalyticsSectionId.KEY_INSIGHTS,              title: 'Key Insights' },
  { id: LendeeAnalyticsSectionId.RECIPIENT_FUNNEL,          title: 'Recipient Conversion Funnel' },
  { id: LendeeAnalyticsSectionId.REQUEST_OFFER_PERFORMANCE, title: 'Request and Offer Performance' },
  { id: LendeeAnalyticsSectionId.SIGNUPS_TREND,             title: 'Recipient Signups Supporter Signups' },
  { id: LendeeAnalyticsSectionId.REQUEST_STATUS,            title: 'Request Status Summary' },
  { id: LendeeAnalyticsSectionId.OFFER_OUTCOME,             title: 'Offer Outcome Analysis' },
  { id: LendeeAnalyticsSectionId.OVERDUE_COLLECTIONS,       title: 'Overdue & Collections Progress' },
  { id: LendeeAnalyticsSectionId.PARTIAL_REPAYMENTS,        title: 'Partial Repayments on Overdue Loans' },
  { id: LendeeAnalyticsSectionId.RECEIVABLES_VS_REVENUE,    title: 'Receivables Completion vs Revenue' },
  { id: LendeeAnalyticsSectionId.RECEIVABLES_RECOVERY,      title: 'Receivables Recovery' },
  { id: LendeeAnalyticsSectionId.DECLINE_REASONS,           title: 'Top Decline Reasons By Supporters' },
];

export const LENDEE_ANALYTICS_CARD_CONFIG: DashboardCardConfig<LendeeAnalyticsSectionId>[] = [
  { id: LendeeAnalyticsSectionId.METRICS_OVERVIEW,          title: 'Metrics Overview',                         defaultOrder: 0,  defaultVisible: true },
  { id: LendeeAnalyticsSectionId.KEY_INSIGHTS,              title: 'Key Insights',                             defaultOrder: 1,  defaultVisible: true },
  { id: LendeeAnalyticsSectionId.RECIPIENT_FUNNEL,          title: 'Recipient Conversion Funnel',              defaultOrder: 2,  defaultVisible: true },
  { id: LendeeAnalyticsSectionId.REQUEST_OFFER_PERFORMANCE, title: 'Request and Offer Performance',            defaultOrder: 3,  defaultVisible: true },
  { id: LendeeAnalyticsSectionId.SIGNUPS_TREND,             title: 'Recipient Signups Supporter Signups',      defaultOrder: 4,  defaultVisible: true },
  { id: LendeeAnalyticsSectionId.REQUEST_STATUS,            title: 'Request Status Summary',                   defaultOrder: 5,  defaultVisible: true },
  { id: LendeeAnalyticsSectionId.OFFER_OUTCOME,             title: 'Offer Outcome Analysis',                   defaultOrder: 6,  defaultVisible: true },
  { id: LendeeAnalyticsSectionId.OVERDUE_COLLECTIONS,       title: 'Overdue & Collections Progress',           defaultOrder: 7,  defaultVisible: true },
  { id: LendeeAnalyticsSectionId.PARTIAL_REPAYMENTS,        title: 'Partial Repayments on Overdue Loans',      defaultOrder: 8,  defaultVisible: true },
  { id: LendeeAnalyticsSectionId.RECEIVABLES_VS_REVENUE,    title: 'Receivables Completion vs Revenue',        defaultOrder: 9,  defaultVisible: true },
  { id: LendeeAnalyticsSectionId.RECEIVABLES_RECOVERY,      title: 'Receivables Recovery',                     defaultOrder: 10, defaultVisible: true },
  { id: LendeeAnalyticsSectionId.DECLINE_REASONS,           title: 'Top Decline Reasons By Supporters',        defaultOrder: 11, defaultVisible: true },
];

const LENDEE_ANALYTICS_KEYS = new Set<string>(LENDEE_ANALYTICS_CARD_CONFIG.map((c) => c.id));
export function isLendeeAnalyticsCardId(key: string): key is LendeeAnalyticsSectionId {
  return LENDEE_ANALYTICS_KEYS.has(key);
}

// ─── Lendee Dashboard (product metrics) ─────────────────────────────────────

export enum LendeeDashboardSectionId {
  REQUESTS_CARD           = 'lend_requests_card',
  OFFERS_CARD             = 'lend_offers_card',
  PAYMENT_PLANS_CARD      = 'lend_payment_plans_card',
  RISK_ASSESSMENT         = 'lend_risk_assessment',
  OPERATIONAL_EFFICIENCY  = 'lend_operational_efficiency',
  KEY_PERFORMANCE_METRICS = 'lend_key_performance_metrics',
}

export const LendeeDashboardSectionDef: SectionDef[] = [
  { id: LendeeDashboardSectionId.REQUESTS_CARD,           title: 'Requests' },
  { id: LendeeDashboardSectionId.OFFERS_CARD,             title: 'Offers' },
  { id: LendeeDashboardSectionId.PAYMENT_PLANS_CARD,      title: 'Payment Plans' },
  { id: LendeeDashboardSectionId.RISK_ASSESSMENT,         title: 'Risk Assessment Matrix' },
  { id: LendeeDashboardSectionId.OPERATIONAL_EFFICIENCY,  title: 'Operational Efficiency' },
  { id: LendeeDashboardSectionId.KEY_PERFORMANCE_METRICS, title: 'Key Performance Metrics' },
];

export const LENDEE_DASHBOARD_CARD_CONFIG: DashboardCardConfig<LendeeDashboardSectionId>[] = [
  { id: LendeeDashboardSectionId.REQUESTS_CARD,           title: 'Requests',                  defaultOrder: 0, defaultVisible: true },
  { id: LendeeDashboardSectionId.OFFERS_CARD,             title: 'Offers',                    defaultOrder: 1, defaultVisible: true },
  { id: LendeeDashboardSectionId.PAYMENT_PLANS_CARD,      title: 'Payment Plans',             defaultOrder: 2, defaultVisible: true },
  { id: LendeeDashboardSectionId.RISK_ASSESSMENT,         title: 'Risk Assessment Matrix',    defaultOrder: 3, defaultVisible: true },
  { id: LendeeDashboardSectionId.OPERATIONAL_EFFICIENCY,  title: 'Operational Efficiency',    defaultOrder: 4, defaultVisible: true },
  { id: LendeeDashboardSectionId.KEY_PERFORMANCE_METRICS, title: 'Key Performance Metrics',   defaultOrder: 5, defaultVisible: true },
];

const LENDEE_DASHBOARD_KEYS = new Set<string>(LENDEE_DASHBOARD_CARD_CONFIG.map((c) => c.id));
export function isLendeeDashboardCardId(key: string): key is LendeeDashboardSectionId {
  return LENDEE_DASHBOARD_KEYS.has(key);
}

// ─── Cool Credit Dashboard ────────────────────────────────────────────────────

export enum CoolCreditDashboardSectionId {
  BUSINESS_GROWTH        = 'cc_business_growth',
  ACTIVATION_AND_CALLS   = 'cc_activation_and_calls',
  PRODUCT_USAGE          = 'cc_product_usage',
  MONETIZATION_REVENUE   = 'cc_monetization_revenue',
  PLAN_LIFECYCLE         = 'cc_plan_lifecycle',
  DISPUTES               = 'cc_disputes',
}

export const CoolCreditDashboardSectionDef: SectionDef[] = [
  { id: CoolCreditDashboardSectionId.BUSINESS_GROWTH,      title: 'Business Growth' },
  { id: CoolCreditDashboardSectionId.ACTIVATION_AND_CALLS, title: 'Activation & Calls' },
  { id: CoolCreditDashboardSectionId.PRODUCT_USAGE,         title: 'Product Usage' },
  { id: CoolCreditDashboardSectionId.MONETIZATION_REVENUE,  title: 'Monetization & Revenue' },
  { id: CoolCreditDashboardSectionId.PLAN_LIFECYCLE,       title: 'Plan Lifecycle' },
  { id: CoolCreditDashboardSectionId.DISPUTES,             title: 'Disputes' },
];

export const COOL_CREDIT_DASHBOARD_CARD_CONFIG: DashboardCardConfig<CoolCreditDashboardSectionId>[] = [
  { id: CoolCreditDashboardSectionId.BUSINESS_GROWTH,      title: 'Business Growth',        defaultOrder: 0, defaultVisible: true },
  { id: CoolCreditDashboardSectionId.ACTIVATION_AND_CALLS, title: 'Activation & Calls',     defaultOrder: 1, defaultVisible: true },
  { id: CoolCreditDashboardSectionId.PRODUCT_USAGE,         title: 'Product Usage',          defaultOrder: 2, defaultVisible: true },
  { id: CoolCreditDashboardSectionId.MONETIZATION_REVENUE,  title: 'Monetization & Revenue', defaultOrder: 3, defaultVisible: true },
  { id: CoolCreditDashboardSectionId.PLAN_LIFECYCLE,       title: 'Plan Lifecycle',         defaultOrder: 4, defaultVisible: true },
  { id: CoolCreditDashboardSectionId.DISPUTES,             title: 'Disputes',               defaultOrder: 5, defaultVisible: true },
];

const COOL_CREDIT_DASHBOARD_KEYS = new Set<string>(COOL_CREDIT_DASHBOARD_CARD_CONFIG.map((c) => c.id));
export function isCoolCreditDashboardCardId(key: string): key is CoolCreditDashboardSectionId {
  return COOL_CREDIT_DASHBOARD_KEYS.has(key);
}

// ─── Cool Credit: Growth, Activation & Revenue ────────────────────────────────

export enum CoolCreditGrowthSectionId {
  STAT_CARDS              = 'cc_gar_stat_cards',
  KEY_INSIGHTS            = 'cc_gar_key_insights',
  DROPOFF_COMPARISON      = 'cc_gar_dropoff_comparison',
  ONBOARDING_FUNNEL       = 'cc_gar_onboarding_funnel',
  REVENUE_CONTRIBUTION    = 'cc_gar_revenue_contribution',
  CBC_EFFECT              = 'cc_gar_cbc_effect',
  PAYMENT_METHOD          = 'cc_gar_payment_method',
  SIMULATOR_USAGE         = 'cc_gar_simulator_usage',
  MONTHLY_DISPUTES_CALLS  = 'cc_gar_monthly_disputes_calls',
  CALL_SOURCE             = 'cc_gar_call_source',
  PLAN_LIFECYCLE          = 'cc_gar_plan_lifecycle',
  PAGE_SESSION            = 'cc_gar_page_session',
}

export const CoolCreditGrowthSectionDef: SectionDef[] = [
  { id: CoolCreditGrowthSectionId.STAT_CARDS,             title: 'Metric Cards' },
  { id: CoolCreditGrowthSectionId.KEY_INSIGHTS,           title: 'Key Insights' },
  { id: CoolCreditGrowthSectionId.DROPOFF_COMPARISON,     title: 'Drop-off Comparison' },
  { id: CoolCreditGrowthSectionId.ONBOARDING_FUNNEL,      title: 'Onboarding Funnel' },
  { id: CoolCreditGrowthSectionId.REVENUE_CONTRIBUTION,   title: 'Revenue Contribution by Plan' },
  { id: CoolCreditGrowthSectionId.CBC_EFFECT,             title: 'CBC Filled vs Credit Fetch' },
  { id: CoolCreditGrowthSectionId.PAYMENT_METHOD,         title: 'Payment Method Distribution' },
  { id: CoolCreditGrowthSectionId.SIMULATOR_USAGE,        title: 'Simulator Usage Distribution' },
  { id: CoolCreditGrowthSectionId.MONTHLY_DISPUTES_CALLS, title: 'Monthly Disputes & Calls Trend' },
  { id: CoolCreditGrowthSectionId.CALL_SOURCE,             title: 'Call Source Distribution' },
  { id: CoolCreditGrowthSectionId.PLAN_LIFECYCLE,         title: 'Plan Lifecycle Status' },
  { id: CoolCreditGrowthSectionId.PAGE_SESSION,           title: 'Page Session Distribution' },
];

export const COOL_CREDIT_GROWTH_CARD_CONFIG: DashboardCardConfig<CoolCreditGrowthSectionId>[] = [
  { id: CoolCreditGrowthSectionId.STAT_CARDS,             title: 'Metric Cards',                        defaultOrder: 0,  defaultVisible: true },
  { id: CoolCreditGrowthSectionId.KEY_INSIGHTS,           title: 'Key Insights',                        defaultOrder: 1,  defaultVisible: true },
  { id: CoolCreditGrowthSectionId.DROPOFF_COMPARISON,     title: 'Drop-off Comparison',                 defaultOrder: 2,  defaultVisible: true },
  { id: CoolCreditGrowthSectionId.ONBOARDING_FUNNEL,      title: 'Onboarding Funnel',                   defaultOrder: 3,  defaultVisible: true },
  { id: CoolCreditGrowthSectionId.REVENUE_CONTRIBUTION,   title: 'Revenue Contribution by Plan',        defaultOrder: 4,  defaultVisible: true },
  { id: CoolCreditGrowthSectionId.CBC_EFFECT,             title: 'CBC Filled vs Credit Fetch',          defaultOrder: 5,  defaultVisible: true },
  { id: CoolCreditGrowthSectionId.PAYMENT_METHOD,         title: 'Payment Method Distribution',         defaultOrder: 6,  defaultVisible: true },
  { id: CoolCreditGrowthSectionId.SIMULATOR_USAGE,        title: 'Simulator Usage Distribution',        defaultOrder: 7,  defaultVisible: true },
  { id: CoolCreditGrowthSectionId.MONTHLY_DISPUTES_CALLS, title: 'Monthly Disputes & Calls Trend',      defaultOrder: 8,  defaultVisible: true },
  { id: CoolCreditGrowthSectionId.CALL_SOURCE,             title: 'Call Source Distribution',            defaultOrder: 9,  defaultVisible: true },
  { id: CoolCreditGrowthSectionId.PLAN_LIFECYCLE,         title: 'Plan Lifecycle Status',               defaultOrder: 10, defaultVisible: true },
  { id: CoolCreditGrowthSectionId.PAGE_SESSION,           title: 'Page Session Distribution',           defaultOrder: 11, defaultVisible: true },
];

const COOL_CREDIT_GROWTH_KEYS = new Set<string>(COOL_CREDIT_GROWTH_CARD_CONFIG.map((c) => c.id));
export function isCoolCreditGrowthCardId(key: string): key is CoolCreditGrowthSectionId {
  return COOL_CREDIT_GROWTH_KEYS.has(key);
}

// ─── Daily Cron Payments ──────────────────────────────────────────────────────

// Values are canonical (`dc_*`, no product prefix). The backend catalog
// ships per-product keys (e.g. Denefits `de_dc_last_attempt` on page id 45).
// `CustomDashboardPinService` resolves them via exact match or
// `widget_key.endsWith('_' + sectionId)` so the same enum works across
// products without hardcoding the 2-char prefix.
export enum DailyCronSectionId {
  LAST_ATTEMPT            = 'dc_last_attempt',
  TRY_AGAIN_OVERVIEW      = 'dc_try_again_overview',
  PAYMENT_LIFECYCLE       = 'dc_payment_lifecycle',
  PAYMENT_ANALYTICS_STATS = 'dc_payment_analytics_stats',
  VOLUME_TRENDS           = 'dc_volume_trends',
  AMOUNT_TRENDS           = 'dc_amount_trends',
  SUCCESS_RATE_TREND      = 'dc_success_rate_trend',
  EFFICIENCY              = 'dc_efficiency',
  RETRY_STRATEGY_IMPACT   = 'dc_retry_strategy_impact',
  RETRY_STRATEGY_TRENDS   = 'dc_retry_strategy_trends',
  CONTRACT_STATE_EVENTS   = 'dc_contract_state_events',
}

export const DailyCronSectionDef: SectionDef[] = [
  { id: DailyCronSectionId.LAST_ATTEMPT,            title: 'Daily Cron Last Attempt' },
  { id: DailyCronSectionId.TRY_AGAIN_OVERVIEW,      title: 'Try Again Cron Last Attempt' },
  { id: DailyCronSectionId.PAYMENT_LIFECYCLE,       title: 'Cron Payment Lifecycle' },
  { id: DailyCronSectionId.PAYMENT_ANALYTICS_STATS, title: 'Payment Analytics Stats' },
  { id: DailyCronSectionId.VOLUME_TRENDS,           title: 'Payment Volume Trends' },
  { id: DailyCronSectionId.AMOUNT_TRENDS,           title: 'Payment Amount Trends' },
  { id: DailyCronSectionId.SUCCESS_RATE_TREND,      title: 'Success Payment Trend' },
  { id: DailyCronSectionId.EFFICIENCY,              title: 'Daily Cron Efficiency' },
  { id: DailyCronSectionId.RETRY_STRATEGY_IMPACT,   title: 'Retry Strategy Impact' },
  { id: DailyCronSectionId.RETRY_STRATEGY_TRENDS,   title: 'Retry Strategy Trends' },
  { id: DailyCronSectionId.CONTRACT_STATE_EVENTS,   title: 'Contract State Events' },
];

export const DAILY_CRON_CARD_CONFIG: DashboardCardConfig<DailyCronSectionId>[] = [
  { id: DailyCronSectionId.LAST_ATTEMPT,            title: 'Daily Cron Last Attempt',     defaultOrder: 0,  defaultVisible: true },
  { id: DailyCronSectionId.TRY_AGAIN_OVERVIEW,      title: 'Try Again Cron Last Attempt', defaultOrder: 1,  defaultVisible: true },
  { id: DailyCronSectionId.PAYMENT_LIFECYCLE,       title: 'Cron Payment Lifecycle',      defaultOrder: 2,  defaultVisible: true },
  { id: DailyCronSectionId.PAYMENT_ANALYTICS_STATS, title: 'Payment Analytics Stats',     defaultOrder: 3,  defaultVisible: true },
  { id: DailyCronSectionId.VOLUME_TRENDS,           title: 'Payment Volume Trends',       defaultOrder: 4,  defaultVisible: true },
  { id: DailyCronSectionId.AMOUNT_TRENDS,           title: 'Payment Amount Trends',       defaultOrder: 5,  defaultVisible: true },
  { id: DailyCronSectionId.SUCCESS_RATE_TREND,      title: 'Success Payment Trend',       defaultOrder: 6,  defaultVisible: true },
  { id: DailyCronSectionId.EFFICIENCY,              title: 'Daily Cron Efficiency',       defaultOrder: 7,  defaultVisible: true },
  { id: DailyCronSectionId.RETRY_STRATEGY_IMPACT,   title: 'Retry Strategy Impact',       defaultOrder: 8,  defaultVisible: true },
  { id: DailyCronSectionId.RETRY_STRATEGY_TRENDS,   title: 'Retry Strategy Trends',       defaultOrder: 9,  defaultVisible: true },
  { id: DailyCronSectionId.CONTRACT_STATE_EVENTS,   title: 'Contract State Events',       defaultOrder: 10, defaultVisible: true },
];

/** Denefits catalog `widget_key` → canonical section id (for display-title lookup). */
export const DAILY_CRON_BACKEND_WIDGET_KEYS: Readonly<
  Partial<Record<DailyCronSectionId, string>>
> = {
  [DailyCronSectionId.LAST_ATTEMPT]:            'de_dc_last_attempt',
  [DailyCronSectionId.TRY_AGAIN_OVERVIEW]:      'de_dc_try_again_overview',
  [DailyCronSectionId.PAYMENT_LIFECYCLE]:       'de_dc_payment_lifecycle',
  [DailyCronSectionId.PAYMENT_ANALYTICS_STATS]: 'de_dc_payment_analytics_stats',
  [DailyCronSectionId.VOLUME_TRENDS]:           'de_dc_volume_trends',
  [DailyCronSectionId.AMOUNT_TRENDS]:           'de_dc_amount_trends',
  [DailyCronSectionId.SUCCESS_RATE_TREND]:      'de_dc_success_rate_trend',
  [DailyCronSectionId.EFFICIENCY]:              'de_dc_efficiency',
  [DailyCronSectionId.RETRY_STRATEGY_IMPACT]:   'de_dc_retry_strategy_impact',
  [DailyCronSectionId.RETRY_STRATEGY_TRENDS]:   'de_dc_retry_strategy_trends',
  [DailyCronSectionId.CONTRACT_STATE_EVENTS]:   'de_dc_contract_state_events',
};

const DAILY_CRON_KEYS = new Set<string>(DAILY_CRON_CARD_CONFIG.map((c) => c.id));
export function isDailyCronCardId(key: string): key is DailyCronSectionId {
  return DAILY_CRON_KEYS.has(key);
}

// ─── Overdue Cron Payments ────────────────────────────────────────────────────

// Values are canonical (`oc_*`, no product prefix). The backend catalog
// ships per-product keys (e.g. Denefits `de_oc_recent_attempt_overview` on
// page id 49). `CustomDashboardPinService` resolves them via exact match or
// `widget_key.endsWith('_' + sectionId)` so the same enum works across
// products without hardcoding the 2-char prefix.
export enum OverdueCronSectionId {
  RECENT_ATTEMPT_OVERVIEW = 'oc_recent_attempt_overview',
  ANALYTICS_SUMMARY       = 'oc_analytics_summary',
  VOLUME_TRENDS           = 'oc_volume_trends',
  TRANSACTION_AMOUNTS     = 'oc_transaction_amounts',
  STATUS_DISTRIBUTION     = 'oc_status_distribution',
  METHOD_DISTRIBUTION     = 'oc_method_distribution',
  REVENUE_DISTRIBUTION    = 'oc_revenue_distribution',
  SUCCESS_RATE_TREND      = 'oc_success_rate_trend',
  DETAILED_STATISTICS     = 'oc_detailed_statistics',
}

export const OverdueCronSectionDef: SectionDef[] = [
  { id: OverdueCronSectionId.RECENT_ATTEMPT_OVERVIEW, title: 'Most Recent Payment Attempt' },
  { id: OverdueCronSectionId.ANALYTICS_SUMMARY,       title: 'Payment Analytics' },
  { id: OverdueCronSectionId.VOLUME_TRENDS,           title: 'Payment Volume Trends' },
  { id: OverdueCronSectionId.TRANSACTION_AMOUNTS,     title: 'Transaction Amounts Over Time' },
  { id: OverdueCronSectionId.STATUS_DISTRIBUTION,     title: 'Payment Status Distribution' },
  { id: OverdueCronSectionId.METHOD_DISTRIBUTION,     title: 'Payment Method Distribution' },
  { id: OverdueCronSectionId.REVENUE_DISTRIBUTION,    title: 'Revenue Distribution' },
  { id: OverdueCronSectionId.SUCCESS_RATE_TREND,      title: 'Success Rate Trend' },
  { id: OverdueCronSectionId.DETAILED_STATISTICS,     title: 'Detailed Payment Statistics' },
];

export const OVERDUE_CRON_CARD_CONFIG: DashboardCardConfig<OverdueCronSectionId>[] = [
  { id: OverdueCronSectionId.RECENT_ATTEMPT_OVERVIEW, title: 'Most Recent Payment Attempt',     defaultOrder: 0, defaultVisible: true },
  { id: OverdueCronSectionId.ANALYTICS_SUMMARY,       title: 'Payment Analytics',               defaultOrder: 1, defaultVisible: true },
  { id: OverdueCronSectionId.VOLUME_TRENDS,           title: 'Payment Volume Trends',           defaultOrder: 2, defaultVisible: true },
  { id: OverdueCronSectionId.TRANSACTION_AMOUNTS,     title: 'Transaction Amounts Over Time',   defaultOrder: 3, defaultVisible: true },
  { id: OverdueCronSectionId.STATUS_DISTRIBUTION,     title: 'Payment Status Distribution',     defaultOrder: 4, defaultVisible: true },
  { id: OverdueCronSectionId.METHOD_DISTRIBUTION,     title: 'Payment Method Distribution',     defaultOrder: 5, defaultVisible: true },
  { id: OverdueCronSectionId.REVENUE_DISTRIBUTION,    title: 'Revenue Distribution',            defaultOrder: 6, defaultVisible: true },
  { id: OverdueCronSectionId.SUCCESS_RATE_TREND,      title: 'Success Rate Trend',              defaultOrder: 7, defaultVisible: true },
  { id: OverdueCronSectionId.DETAILED_STATISTICS,     title: 'Detailed Payment Statistics',     defaultOrder: 8, defaultVisible: true },
];

/** Denefits catalog `widget_key` → canonical section id (for display-title lookup). */
export const OVERDUE_CRON_BACKEND_WIDGET_KEYS: Readonly<
  Partial<Record<OverdueCronSectionId, string>>
> = {
  [OverdueCronSectionId.RECENT_ATTEMPT_OVERVIEW]: 'de_oc_recent_attempt_overview',
  [OverdueCronSectionId.ANALYTICS_SUMMARY]:       'de_oc_analytics_summary',
  [OverdueCronSectionId.VOLUME_TRENDS]:           'de_oc_volume_trends',
  [OverdueCronSectionId.TRANSACTION_AMOUNTS]:     'de_oc_transaction_amounts',
  [OverdueCronSectionId.STATUS_DISTRIBUTION]:     'de_oc_status_distribution',
  [OverdueCronSectionId.METHOD_DISTRIBUTION]:     'de_oc_method_distribution',
  [OverdueCronSectionId.REVENUE_DISTRIBUTION]:    'de_oc_revenue_distribution',
  [OverdueCronSectionId.SUCCESS_RATE_TREND]:      'de_oc_success_rate_trend',
  [OverdueCronSectionId.DETAILED_STATISTICS]:     'de_oc_detailed_statistics',
};

const OVERDUE_CRON_KEYS = new Set<string>(OVERDUE_CRON_CARD_CONFIG.map((c) => c.id));
export function isOverdueCronCardId(key: string): key is OverdueCronSectionId {
  return OVERDUE_CRON_KEYS.has(key);
}

// ─── Arrangement Cron Payments ────────────────────────────────────────────────

// Values are canonical (`ac_*`, no product prefix) so the
// `CustomDashboardPinService` suffix matcher
// (`widget_key.endsWith('_' + sectionId)`) resolves the per-product
// `widgets.id` if/when the backend catalog starts advertising this page.
//
// The page is a tabbed analytics view (Overview, Trends and Recovery,
// Admin Performance). Each tab is treated as a top-level customizable
// section so the user can reorder / hide tabs from the layout editor
// AND pin individual tab views to their custom dashboard.
//
// Until backend exposes Arrangement Cron Payments in the dashboards
// catalog, `effectiveCatalogPageId` resolves to `null` on the page and
// the pin-to-custom-dashboard backend round-trip is skipped, while the
// Customize Layout editor still works locally (localStorage-backed) —
// so adding the page here is forward-looking and never blocks UX.
export enum ArrangmentCronSectionId {
  OVERVIEW          = 'ac_overview',
  TRENDS_RECOVERY   = 'ac_trends_recovery',
  ADMIN_PERFORMANCE = 'ac_admin_performance',
}

export const ArrangmentCronSectionDef: SectionDef[] = [
  { id: ArrangmentCronSectionId.OVERVIEW,          title: 'Overview' },
  { id: ArrangmentCronSectionId.TRENDS_RECOVERY,   title: 'Trends and Recovery' },
  { id: ArrangmentCronSectionId.ADMIN_PERFORMANCE, title: 'Admin Performance' },
];

export const ARRANGEMENT_CRON_CARD_CONFIG: DashboardCardConfig<ArrangmentCronSectionId>[] = [
  { id: ArrangmentCronSectionId.OVERVIEW,          title: 'Overview',             defaultOrder: 0, defaultVisible: true },
  { id: ArrangmentCronSectionId.TRENDS_RECOVERY,   title: 'Trends and Recovery',  defaultOrder: 1, defaultVisible: true },
  { id: ArrangmentCronSectionId.ADMIN_PERFORMANCE, title: 'Admin Performance',    defaultOrder: 2, defaultVisible: true },
];

const ARRANGEMENT_CRON_KEYS = new Set<string>(ARRANGEMENT_CRON_CARD_CONFIG.map((c) => c.id));
export function isArrangmentCronCardId(key: string): key is ArrangmentCronSectionId {
  return ARRANGEMENT_CRON_KEYS.has(key);
}

// visionboard business performance report
export enum BusinessPerformanceReportSectionId {
  BUSINESSES_REGISTERED       = 'businesses_registered',
  BUSINESS_STATE_ACTIVE       = 'business_state_active',
  BUSINESS_STATE_INACTIVE     = 'business_state_inactive',
  BUSINESS_STATE_ACTIVATED    = 'business_state_activated',
  BUSINESS_STATE_REVIVED      = 'business_state_revived',
  BUSINESS_STAGE              = 'business_stage',
  BUSINESS_HIGHLIGHTS         = 'business_highlights',
  TRENDING_INDUSTRIES         = 'trending_industries',
  BUSINESS_STATEWISE          = 'business_statewise',
  BUSINESS_PHASE              = 'business_phase',
  ACTIVE_BUSINESSES           = 'active_businesses',
  BUSINESS_COHORT_GROUP       = 'business_cohort_group',
}
export const BusinessPerformanceReportSectionDef: SectionDef[] = [
  { id: BusinessPerformanceReportSectionId.BUSINESSES_REGISTERED,    title: 'Businesses Registered Stats' },
  { id: BusinessPerformanceReportSectionId.BUSINESS_STATE_ACTIVE,    title: 'Active Business' },
  { id: BusinessPerformanceReportSectionId.BUSINESS_STATE_INACTIVE,  title: 'Inactive Business' },
  { id: BusinessPerformanceReportSectionId.BUSINESS_STATE_ACTIVATED, title: 'Business Activated' },
  { id: BusinessPerformanceReportSectionId.BUSINESS_STATE_REVIVED,   title: 'Business Revived' },
  { id: BusinessPerformanceReportSectionId.BUSINESS_STAGE,           title: 'Business Stage Stats' },
  { id: BusinessPerformanceReportSectionId.BUSINESS_HIGHLIGHTS,      title: 'Business Highlights' },
  { id: BusinessPerformanceReportSectionId.TRENDING_INDUSTRIES,      title: 'Trending Industries' },
  { id: BusinessPerformanceReportSectionId.BUSINESS_STATEWISE,       title: 'Business Statewise Stats' },
  { id: BusinessPerformanceReportSectionId.BUSINESS_PHASE,           title: 'Business Phase Stats' },
  { id: BusinessPerformanceReportSectionId.ACTIVE_BUSINESSES,        title: 'Active Businesses Stats' },
  { id: BusinessPerformanceReportSectionId.BUSINESS_COHORT_GROUP,    title: 'Business Cohort Group' },
];

export const BUSINESS_PERFORMANCE_REPORT_CARD_CONFIG: DashboardCardConfig<BusinessPerformanceReportSectionId>[] = [
  { id: BusinessPerformanceReportSectionId.BUSINESSES_REGISTERED,    title: 'Businesses Registered Stats', defaultOrder: 0, defaultVisible: true },
  { id: BusinessPerformanceReportSectionId.BUSINESS_STATE_ACTIVE,    title: 'Active Business', defaultOrder: 1, defaultVisible: true },
  { id: BusinessPerformanceReportSectionId.BUSINESS_STATE_INACTIVE,  title: 'Inactive Business', defaultOrder: 2, defaultVisible: true },
  { id: BusinessPerformanceReportSectionId.BUSINESS_STATE_ACTIVATED, title: 'Business Activated', defaultOrder: 3, defaultVisible: true },
  { id: BusinessPerformanceReportSectionId.BUSINESS_STATE_REVIVED,   title: 'Business Revived', defaultOrder: 4, defaultVisible: true },
  { id: BusinessPerformanceReportSectionId.BUSINESS_STAGE,           title: 'Business Stage Stats', defaultOrder: 5, defaultVisible: true },
  { id: BusinessPerformanceReportSectionId.BUSINESS_HIGHLIGHTS,      title: 'Business Highlights', defaultOrder: 6, defaultVisible: true },
  { id: BusinessPerformanceReportSectionId.TRENDING_INDUSTRIES,      title: 'Trending Industries', defaultOrder: 7, defaultVisible: true },
  { id: BusinessPerformanceReportSectionId.BUSINESS_STATEWISE,       title: 'Business Statewise Stats', defaultOrder: 8, defaultVisible: true },
  { id: BusinessPerformanceReportSectionId.BUSINESS_PHASE,           title: 'Business Phase Stats', defaultOrder: 9, defaultVisible: true },
  { id: BusinessPerformanceReportSectionId.ACTIVE_BUSINESSES,        title: 'Active Businesses Stats', defaultOrder: 10, defaultVisible: true },
  { id: BusinessPerformanceReportSectionId.BUSINESS_COHORT_GROUP,    title: 'Business Cohort Group', defaultOrder: 11, defaultVisible: true },
];

const BUSINESS_PERFORMANCE_REPORT_KEYS = new Set<string>(BUSINESS_PERFORMANCE_REPORT_CARD_CONFIG.map((c) => c.id));
export function isBusinessPerformanceReportCardId(key: string): key is BusinessPerformanceReportSectionId {
  return BUSINESS_PERFORMANCE_REPORT_KEYS.has(key);
}

// visionboard contracts report
export enum ContractsReportSectionId {
  CONTRACT_COUNT_STATS                 = 'contract_count_stats',
  CONTRACT_STATUS                      = 'contract_status',
  STATE_CONTRACTS                      = 'state_contracts',
  PLATFORM_TOP_CONTRACTS_PLATFORMS     = 'platform_top_contracts_platforms',
  PLATFORM_AVERAGE_CONTRACT_VALUE      = 'platform_average_contract_value',
  CONTRACT_ENROLLMENT_COLLECTED        = 'contract_enrollment_collected',
  CONTRACT_UPFRONT_COLLECTED           = 'contract_upfront_collected',
  CONTRACT_LATE_FEE_COLLECTED          = 'contract_late_fee_collected',
  CONTRACT_CHANGE_PAYMENT_DATE         = 'contract_change_payment_date',
  CONTRACT_DOWNPAYMENT_COLLECTED       = 'contract_downpayment_collected',
  CONTRACT_EDIT_CONTRACT               = 'contract_edit_contract',
  CONTRACT_REVERSALS                   = 'contract_reversals',
  CONTRACT_REFUNDS                     = 'contract_refunds',
  CONTRACT_LATE_FEE_REFUNDED           = 'contract_late_fee_refunded',      
  CONTRACT_SETTLED_CONTRACTS           = 'contract_settled_contracts',
  CONTRACT_STATE                       = 'contract_state',
  CONTRACT_PROTECTED_PENDING           = 'contract_protected_pending',
  CONTRACT_PROTECTED_CONTRACTS         = 'contract_protected_contracts',
  CONTRACT_NON_PROTECTED_CONTRACTS     = 'contract_non_protected_contracts',
}
export const ContractsReportSectionDef: SectionDef[] = [
  { id: ContractsReportSectionId.CONTRACT_COUNT_STATS,             title: 'Contract Count Stats' },
  { id: ContractsReportSectionId.CONTRACT_STATUS,                  title: 'Contract Status' },
  { id: ContractsReportSectionId.STATE_CONTRACTS,                  title: 'State Contracts' },
  { id: ContractsReportSectionId.PLATFORM_TOP_CONTRACTS_PLATFORMS, title: 'Top Contracts Platforms' },
  { id: ContractsReportSectionId.PLATFORM_AVERAGE_CONTRACT_VALUE,  title: 'Average Contract Value' }, 
  { id: ContractsReportSectionId.CONTRACT_ENROLLMENT_COLLECTED,    title: 'Enrollment Collected' },
  { id: ContractsReportSectionId.CONTRACT_UPFRONT_COLLECTED,       title: 'Upfront Collected' },
  { id: ContractsReportSectionId.CONTRACT_LATE_FEE_COLLECTED,      title: 'Late Fee Collected' },
  { id: ContractsReportSectionId.CONTRACT_CHANGE_PAYMENT_DATE,     title: 'Change Payment Date Fee Collected' },
  { id: ContractsReportSectionId.CONTRACT_DOWNPAYMENT_COLLECTED,   title: 'Downpayment Collected' },
  { id: ContractsReportSectionId.CONTRACT_EDIT_CONTRACT,           title: 'Edit Contract' },
  { id: ContractsReportSectionId.CONTRACT_REVERSALS,               title: 'Reversals' },
  { id: ContractsReportSectionId.CONTRACT_REFUNDS,                 title: 'Refunds' },
  { id: ContractsReportSectionId.CONTRACT_LATE_FEE_REFUNDED,       title: 'Late Fee Refunded' },
  { id: ContractsReportSectionId.CONTRACT_SETTLED_CONTRACTS,       title: 'Settled Contracts' },
  { id: ContractsReportSectionId.CONTRACT_STATE,                   title: 'Contract State' },
  { id: ContractsReportSectionId.CONTRACT_PROTECTED_PENDING,       title: 'Protected Status Pending' },
  { id: ContractsReportSectionId.CONTRACT_PROTECTED_CONTRACTS,     title: 'Protected Contracts' },
  { id: ContractsReportSectionId.CONTRACT_NON_PROTECTED_CONTRACTS, title: 'Non-protected Contracts' },
];

export const CONTRACTS_REPORT_CARD_CONFIG: DashboardCardConfig<ContractsReportSectionId>[] = [
  { id: ContractsReportSectionId.CONTRACT_COUNT_STATS,             title: 'Contract Count Stats', defaultOrder: 0, defaultVisible: true },
  { id: ContractsReportSectionId.CONTRACT_STATUS,                  title: 'Contract Status', defaultOrder: 1, defaultVisible: true },
  { id: ContractsReportSectionId.STATE_CONTRACTS,                  title: 'State Contracts', defaultOrder: 2, defaultVisible: true },
  { id: ContractsReportSectionId.PLATFORM_TOP_CONTRACTS_PLATFORMS, title: 'Top Contracts Platforms', defaultOrder: 3, defaultVisible: true },
  { id: ContractsReportSectionId.PLATFORM_AVERAGE_CONTRACT_VALUE,  title: 'Average Contract Value', defaultOrder: 4, defaultVisible: true },
  { id: ContractsReportSectionId.CONTRACT_ENROLLMENT_COLLECTED,    title: 'Enrollment Collected', defaultOrder: 5, defaultVisible: true },
  { id: ContractsReportSectionId.CONTRACT_UPFRONT_COLLECTED,       title: 'Upfront Collected', defaultOrder: 6, defaultVisible: true },
  { id: ContractsReportSectionId.CONTRACT_LATE_FEE_COLLECTED,      title: 'Late Fee Collected', defaultOrder: 7, defaultVisible: true },
  { id: ContractsReportSectionId.CONTRACT_CHANGE_PAYMENT_DATE,     title: 'Change Payment Date Fee Collected', defaultOrder: 8, defaultVisible: true },
  { id: ContractsReportSectionId.CONTRACT_DOWNPAYMENT_COLLECTED,   title: 'Downpayment Collected', defaultOrder: 9, defaultVisible: true },
  { id: ContractsReportSectionId.CONTRACT_EDIT_CONTRACT,           title: 'Edit Contract', defaultOrder: 10, defaultVisible: true },
  { id: ContractsReportSectionId.CONTRACT_REVERSALS,               title: 'Reversals', defaultOrder: 11, defaultVisible: true },
  { id: ContractsReportSectionId.CONTRACT_REFUNDS,                 title: 'Refunds', defaultOrder: 12, defaultVisible: true },
  { id: ContractsReportSectionId.CONTRACT_LATE_FEE_REFUNDED,       title: 'Late Fee Refunded', defaultOrder: 13, defaultVisible: true },
  { id: ContractsReportSectionId.CONTRACT_SETTLED_CONTRACTS,       title: 'Settled Contracts', defaultOrder: 14, defaultVisible: true },
  { id: ContractsReportSectionId.CONTRACT_STATE,                   title: 'Contract State', defaultOrder: 15, defaultVisible: true },
  { id: ContractsReportSectionId.CONTRACT_PROTECTED_PENDING,       title: 'Protected Status Pending', defaultOrder: 16, defaultVisible: true },   
  { id: ContractsReportSectionId.CONTRACT_PROTECTED_CONTRACTS,     title: 'Protected Contracts', defaultOrder: 17, defaultVisible: true },
  { id: ContractsReportSectionId.CONTRACT_NON_PROTECTED_CONTRACTS, title: 'Non-protected Contracts', defaultOrder: 18, defaultVisible: true },
];

const CONTRACTS_REPORT_KEYS = new Set<string>(CONTRACTS_REPORT_CARD_CONFIG.map((c) => c.id));
export function isContractsReportCardId(key: string): key is ContractsReportSectionId {
  return CONTRACTS_REPORT_KEYS.has(key);
}
// ─── Arrangement Cron — Overview tab subsections ─────────────────────────────

// Values MUST match backend `widgets.widget_key` on page slug `arrangement-cron`.
export enum ArrangementCronOverviewSectionId {
  SYSTEM_INTEL            = 'ac_system_intel',
  SCHEDULED_TODAY         = 'ac_scheduled_today',
  RECENT_ATTEMPTS         = 'ac_recent_attempts',
  KPI_SUMMARY             = 'ac_kpi_summary',
  TIME_GAP_DISTRIBUTION   = 'ac_time_gap_distribution',
  QUICK_ACTION            = 'ac_quick_action',
  AR_AGING_OVERVIEW       = 'ac_ar_aging_overview',
  MONTHLY_DISTRIBUTION    = 'ac_monthly_distribution',
  CREATION_LIFECYCLE      = 'ac_creation_lifecycle',
  CONTRACT_STATUS_CHART   = 'ac_contract_status_chart',
  METRIC_ANOMALIES        = 'ac_metric_anomalies',
  ADMIN_DUPLICATES_TABLE  = 'ac_admin_duplicates_table',
}

export const ArrangementCronOverviewSectionDef: SectionDef[] = [
  { id: ArrangementCronOverviewSectionId.SYSTEM_INTEL,           title: 'System Intel Arrangement Cron' },
  { id: ArrangementCronOverviewSectionId.SCHEDULED_TODAY,         title: 'Scheduled Today' },
  { id: ArrangementCronOverviewSectionId.RECENT_ATTEMPTS,         title: 'Recent Attempts' },
  { id: ArrangementCronOverviewSectionId.KPI_SUMMARY,            title: 'Arrangement Core KPI Summary Cards' },
  { id: ArrangementCronOverviewSectionId.TIME_GAP_DISTRIBUTION,   title: 'Average Distribution by Time Gap Cards' },
  { id: ArrangementCronOverviewSectionId.QUICK_ACTION,            title: 'Quick Action' },
  { id: ArrangementCronOverviewSectionId.AR_AGING_OVERVIEW,       title: 'Accounts Receivable Aging Overview' },
  { id: ArrangementCronOverviewSectionId.MONTHLY_DISTRIBUTION,    title: 'Arrangement Distribution' },
  { id: ArrangementCronOverviewSectionId.CREATION_LIFECYCLE,      title: 'Creation Date Lifecycle' },
  { id: ArrangementCronOverviewSectionId.CONTRACT_STATUS_CHART,  title: 'Arrangements by Contract Status' },
  { id: ArrangementCronOverviewSectionId.METRIC_ANOMALIES,        title: 'System Metric Anomalies (Duplicates and Deletions)' },
  { id: ArrangementCronOverviewSectionId.ADMIN_DUPLICATES_TABLE,  title: 'Duplicates by Admin Breakdown Table' },
];

export const ARRANGEMENT_CRON_OVERVIEW_CARD_CONFIG: DashboardCardConfig<ArrangementCronOverviewSectionId>[] = [
  { id: ArrangementCronOverviewSectionId.SYSTEM_INTEL,           title: 'System Intel Arrangement Cron',                        defaultOrder: 0,  defaultVisible: true },
  { id: ArrangementCronOverviewSectionId.SCHEDULED_TODAY,         title: 'Scheduled Today',                                      defaultOrder: 1,  defaultVisible: true },
  { id: ArrangementCronOverviewSectionId.RECENT_ATTEMPTS,         title: 'Recent Attempts',                                      defaultOrder: 2,  defaultVisible: true },
  { id: ArrangementCronOverviewSectionId.KPI_SUMMARY,             title: 'Arrangement Core KPI Summary Cards',                   defaultOrder: 3,  defaultVisible: true },
  { id: ArrangementCronOverviewSectionId.TIME_GAP_DISTRIBUTION,   title: 'Average Distribution by Time Gap Cards',               defaultOrder: 4,  defaultVisible: true },
  { id: ArrangementCronOverviewSectionId.QUICK_ACTION,            title: 'Quick Action',                                         defaultOrder: 5,  defaultVisible: true },
  { id: ArrangementCronOverviewSectionId.AR_AGING_OVERVIEW,       title: 'Accounts Receivable Aging Overview',                   defaultOrder: 6,  defaultVisible: true },
  { id: ArrangementCronOverviewSectionId.MONTHLY_DISTRIBUTION,    title: 'Arrangement Distribution',                             defaultOrder: 7,  defaultVisible: true },
  { id: ArrangementCronOverviewSectionId.CREATION_LIFECYCLE,      title: 'Creation Date Lifecycle',                              defaultOrder: 8,  defaultVisible: true },
  { id: ArrangementCronOverviewSectionId.CONTRACT_STATUS_CHART,  title: 'Arrangements by Contract Status',                      defaultOrder: 9,  defaultVisible: true },
  { id: ArrangementCronOverviewSectionId.METRIC_ANOMALIES,        title: 'System Metric Anomalies (Duplicates and Deletions)',   defaultOrder: 10, defaultVisible: true },
  { id: ArrangementCronOverviewSectionId.ADMIN_DUPLICATES_TABLE,  title: 'Duplicates by Admin Breakdown Table',                  defaultOrder: 11, defaultVisible: true },
];

const ARRANGEMENT_CRON_OVERVIEW_KEYS = new Set<string>(
  ARRANGEMENT_CRON_OVERVIEW_CARD_CONFIG.map((c) => c.id),
);
export function isArrangementCronOverviewSectionId(
  key: string,
): key is ArrangementCronOverviewSectionId {
  return ARRANGEMENT_CRON_OVERVIEW_KEYS.has(key);
}

/** Denefits catalog `widget_key` → canonical section id (for embed + display-title lookup). */
export const ARRANGEMENT_CRON_OVERVIEW_BACKEND_WIDGET_KEYS: Readonly<
  Partial<Record<ArrangementCronOverviewSectionId, string>>
> = {
  [ArrangementCronOverviewSectionId.SYSTEM_INTEL]:           'de_ac_system_intel',
  [ArrangementCronOverviewSectionId.SCHEDULED_TODAY]:         'de_ac_scheduled_today',
  [ArrangementCronOverviewSectionId.RECENT_ATTEMPTS]:         'de_ac_recent_attempts',
  [ArrangementCronOverviewSectionId.KPI_SUMMARY]:             'de_ac_kpi_summary',
  [ArrangementCronOverviewSectionId.TIME_GAP_DISTRIBUTION]:   'de_ac_time_gap_distribution',
  [ArrangementCronOverviewSectionId.QUICK_ACTION]:            'de_ac_quick_action',
  [ArrangementCronOverviewSectionId.AR_AGING_OVERVIEW]:       'de_ac_ar_aging_overview',
  [ArrangementCronOverviewSectionId.MONTHLY_DISTRIBUTION]:    'de_ac_monthly_distribution',
  [ArrangementCronOverviewSectionId.CREATION_LIFECYCLE]:      'de_ac_creation_lifecycle',
  [ArrangementCronOverviewSectionId.CONTRACT_STATUS_CHART]:  'de_ac_contract_status_chart',
  [ArrangementCronOverviewSectionId.METRIC_ANOMALIES]:        'de_ac_metric_anomalies',
  [ArrangementCronOverviewSectionId.ADMIN_DUPLICATES_TABLE]:  'de_ac_admin_duplicates_table',
};

// ─── Arrangement Cron — Trends tab subsections ───────────────────────────────

// Values MUST match backend `widgets.widget_key` on page slug
// `arrangement-cron-trends-and-recovery`.
export enum ArrangementCronTrendsSectionId {
  STATS                          = 'ac_tr_stats',
  RECOVERY_AMOUNT                  = 'ac_tr_recovery_amount',
  CRITICAL_INSIGHTS                = 'ac_tr_critical_insights',
  MONTH_RECOVERY_PERFORMANCE       = 'ac_tr_month_recover_performance',
  ARRANGEMENT_TYPES                = 'ac_tr_arrangement_type',
  ARRANGEMENT_CATEGORIES           = 'ac_tr_arrangement_categories',
  SUCCESS_FAILURE_TIME             = 'ac_tr_success_failure_time',
  SCHEDULE_RECOVERED_AMOUNT        = 'ac_tr_schedule_recovered_amount',
  SUCCESS_RATE_COMPARISON_BY_TYPE  = 'ac_tr_success_rate_comparison_by_type',
  RECOVERY_VALUE_ATTEMPT           = 'ac_tr_recovery_value_attempt',
  RECOVERY_EXECUTION_TIMING        = 'ac_tr_recovery_execution_timing',
  YOY_PERFORMANCE                  = 'ac_tr_yoy_performance',
}

export const ArrangementCronTrendsSectionDef: SectionDef[] = [
  { id: ArrangementCronTrendsSectionId.STATS,                          title: 'Total stats' },
  { id: ArrangementCronTrendsSectionId.RECOVERY_AMOUNT,              title: 'Volume Success Recovery Amount' },
  { id: ArrangementCronTrendsSectionId.CRITICAL_INSIGHTS,            title: 'Critical Insights & Opportunities' },
  { id: ArrangementCronTrendsSectionId.MONTH_RECOVERY_PERFORMANCE,    title: 'Monthly Recovery Performance' },
  { id: ArrangementCronTrendsSectionId.ARRANGEMENT_TYPES,              title: 'Arrangement Types' },
  { id: ArrangementCronTrendsSectionId.SUCCESS_FAILURE_TIME,         title: 'Success vs Failure by Time Slot' },
  { id: ArrangementCronTrendsSectionId.ARRANGEMENT_CATEGORIES,         title: 'Arrangement Categories' },
  { id: ArrangementCronTrendsSectionId.SCHEDULE_RECOVERED_AMOUNT,     title: 'Daily Scheduled Vs Recovered Amount' },
  { id: ArrangementCronTrendsSectionId.SUCCESS_RATE_COMPARISON_BY_TYPE, title: 'Success Rate Comparison By Type' },
  { id: ArrangementCronTrendsSectionId.RECOVERY_VALUE_ATTEMPT,        title: 'Recovery Value by Arrangement Attempt' },
  { id: ArrangementCronTrendsSectionId.RECOVERY_EXECUTION_TIMING,     title: 'Recovery by Execution Timing' },
  { id: ArrangementCronTrendsSectionId.YOY_PERFORMANCE,               title: 'Year-Over-Year Performance' },
];

export const ARRANGEMENT_CRON_TRENDS_CARD_CONFIG: DashboardCardConfig<ArrangementCronTrendsSectionId>[] = [
  { id: ArrangementCronTrendsSectionId.STATS,                          title: 'Total stats',                              defaultOrder: 0,  defaultVisible: true },
  { id: ArrangementCronTrendsSectionId.RECOVERY_AMOUNT,              title: 'Volume Success Recovery Amount',           defaultOrder: 1,  defaultVisible: true },
  { id: ArrangementCronTrendsSectionId.CRITICAL_INSIGHTS,            title: 'Critical Insights & Opportunities',          defaultOrder: 2,  defaultVisible: true },
  { id: ArrangementCronTrendsSectionId.MONTH_RECOVERY_PERFORMANCE,    title: 'Monthly Recovery Performance',             defaultOrder: 3,  defaultVisible: true },
  { id: ArrangementCronTrendsSectionId.ARRANGEMENT_TYPES,              title: 'Arrangement Types',                        defaultOrder: 4,  defaultVisible: true },
  { id: ArrangementCronTrendsSectionId.SUCCESS_FAILURE_TIME,         title: 'Success vs Failure by Time Slot',          defaultOrder: 5,  defaultVisible: true },
  { id: ArrangementCronTrendsSectionId.SCHEDULE_RECOVERED_AMOUNT,     title: 'Daily Scheduled Vs Recovered Amount',      defaultOrder: 6,  defaultVisible: true },
  { id: ArrangementCronTrendsSectionId.SUCCESS_RATE_COMPARISON_BY_TYPE, title: 'Success Rate Comparison By Type',      defaultOrder: 7,  defaultVisible: true },
  { id: ArrangementCronTrendsSectionId.ARRANGEMENT_CATEGORIES,         title: 'Arrangement Categories',                   defaultOrder: 8,  defaultVisible: true },
  { id: ArrangementCronTrendsSectionId.RECOVERY_VALUE_ATTEMPT,        title: 'Recovery Value by Arrangement Attempt',    defaultOrder: 9,  defaultVisible: true },
  { id: ArrangementCronTrendsSectionId.RECOVERY_EXECUTION_TIMING,     title: 'Recovery by Execution Timing',             defaultOrder: 10, defaultVisible: true },
  { id: ArrangementCronTrendsSectionId.YOY_PERFORMANCE,               title: 'Year-Over-Year Performance',               defaultOrder: 11, defaultVisible: true },
];

const ARRANGEMENT_CRON_TRENDS_KEYS = new Set<string>(
  ARRANGEMENT_CRON_TRENDS_CARD_CONFIG.map((c) => c.id),
);
export function isArrangementCronTrendsSectionId(
  key: string,
): key is ArrangementCronTrendsSectionId {
  return ARRANGEMENT_CRON_TRENDS_KEYS.has(key);
}

/** Denefits catalog `widget_key` → canonical section id (for embed + display-title lookup). */
export const ARRANGEMENT_CRON_TRENDS_BACKEND_WIDGET_KEYS: Readonly<
  Partial<Record<ArrangementCronTrendsSectionId, string>>
> = {
  [ArrangementCronTrendsSectionId.STATS]:                         'de_ac_tr_stats',
  [ArrangementCronTrendsSectionId.RECOVERY_AMOUNT]:               'de_ac_tr_recovery_amount',
  [ArrangementCronTrendsSectionId.CRITICAL_INSIGHTS]:             'de_ac_tr_critical_insights',
  [ArrangementCronTrendsSectionId.MONTH_RECOVERY_PERFORMANCE]:    'de_ac_tr_month_recover_performance',
  [ArrangementCronTrendsSectionId.ARRANGEMENT_TYPES]:             'de_ac_tr_arrangement_type',
  [ArrangementCronTrendsSectionId.SUCCESS_FAILURE_TIME]:          'de_ac_tr_success_failure_time',
  [ArrangementCronTrendsSectionId.SCHEDULE_RECOVERED_AMOUNT]:     'de_ac_tr_schedule_recovered_amount',
  [ArrangementCronTrendsSectionId.SUCCESS_RATE_COMPARISON_BY_TYPE]: 'de_ac_tr_success_rate_comparison_by_type',
  [ArrangementCronTrendsSectionId.ARRANGEMENT_CATEGORIES]:        'de_ac_tr_arrangement_categories',
  [ArrangementCronTrendsSectionId.RECOVERY_VALUE_ATTEMPT]:        'de_ac_tr_recovery_value_attempt',
  [ArrangementCronTrendsSectionId.RECOVERY_EXECUTION_TIMING]:     'de_ac_tr_recovery_execution_timing',
  [ArrangementCronTrendsSectionId.YOY_PERFORMANCE]:              'de_ac_tr_yoy_performance',
};

// ─── Arrangement Cron — Admin Performance tab subsections ────────────────────

// Values MUST match backend `widgets.widget_key` on page slug
// `arrangement-cron-admin-performance`.
export enum ArrangementCronAdminPerformanceSectionId {
  STATS_SUMMARY_OVERVIEW              = 'ac_ap_stats_summary_overview',
  TOP_PERFORMER                       = 'ac_ap_top_performer',
  DUPLICATE_CRISIS                    = 'ac_ap_duplicate_crisis',
  AVG_RECOVERY_PER_ADMIN              = 'ac_ap_avg_recovery_per_admin',
  DUPLICATE_ENTRY_TREND_OVER_TIME     = 'ac_ap_duplicate_entry_trend_over_time',
  DUPLICATE_TREND_DOD                 = 'ac_ap_duplicate_trend_dod',
  PERFORMANCE_DISTRIBUTION_SUCCESS    = 'ac_ap_performance_distribution_admin_success_rate',
  RECOVERY_EFFICIENCY                 = 'ac_ap_recovery_efficiency',
  REPEAT_ARRANGEMENT_PATTERN          = 'ac_ap_repeat_arrangement_pattern_analysis',
  ADMIN_WORKLOAD_VS_RECOVERY          = 'ac_ap_admin_workload_vs_recovery_output',
  ADMIN_PRODUCTIVITY_VS_RECOVERY      = 'ac_ap_admin_productivity_vs_recovery_yield',
  DUPLICATE_ENTRIES_BY_ADMIN          = 'ac_ap_duplicate_entries_by_admin',
  REPEAT_ARRANGEMENT_INTENSITY_DOD    = 'ac_ap_repeat_arrangement_intensity_dod',
  ADMIN_DUPLICATE_EXPOSURE            = 'ac_ap_admin_duplicate_exposure',
  DETAILED_DUPLICATE_ANALYSIS         = 'ac_ap_detailed_duplicate_analysis',
  WORST_YEAR                          = 'ac_ap_worst_year',
  BEST_YEAR                           = 'ac_ap_best_year',
  LATEST_YEAR                         = 'ac_ap_latest_year',
}

export const ArrangementCronAdminPerformanceSectionDef: SectionDef[] = [
  { id: ArrangementCronAdminPerformanceSectionId.STATS_SUMMARY_OVERVIEW,         title: 'Admin Performance Stats Summary Overview' },
  { id: ArrangementCronAdminPerformanceSectionId.TOP_PERFORMER,                  title: 'Top Performer' },
  { id: ArrangementCronAdminPerformanceSectionId.DUPLICATE_CRISIS,               title: 'Duplicate Crisis' },
  { id: ArrangementCronAdminPerformanceSectionId.AVG_RECOVERY_PER_ADMIN,         title: 'Avg. Recovery Per Admin' },
  { id: ArrangementCronAdminPerformanceSectionId.DUPLICATE_ENTRY_TREND_OVER_TIME, title: 'Duplicate Entry Trend Over Time' },
  { id: ArrangementCronAdminPerformanceSectionId.DUPLICATE_TREND_DOD,             title: 'Duplicate Trend DoD' },
  { id: ArrangementCronAdminPerformanceSectionId.PERFORMANCE_DISTRIBUTION_SUCCESS, title: 'Performance Distribution - Admin Success Rate' },
  { id: ArrangementCronAdminPerformanceSectionId.RECOVERY_EFFICIENCY,            title: 'Recovery Efficiency' },
  { id: ArrangementCronAdminPerformanceSectionId.REPEAT_ARRANGEMENT_PATTERN,      title: 'Repeat Arrangement Pattern Analysis' },
  { id: ArrangementCronAdminPerformanceSectionId.ADMIN_WORKLOAD_VS_RECOVERY,    title: 'Admin Workload vs Recovery Output' },
  { id: ArrangementCronAdminPerformanceSectionId.ADMIN_PRODUCTIVITY_VS_RECOVERY,  title: 'Admin Productivity vs Recovery Yield' },
  { id: ArrangementCronAdminPerformanceSectionId.DUPLICATE_ENTRIES_BY_ADMIN,     title: 'Duplicate Entries by Admin (Top 5)' },
  { id: ArrangementCronAdminPerformanceSectionId.REPEAT_ARRANGEMENT_INTENSITY_DOD, title: 'Repeat Arrangement Intensity DoD' },
  { id: ArrangementCronAdminPerformanceSectionId.ADMIN_DUPLICATE_EXPOSURE,       title: 'Admin Duplicate Exposure' },
  { id: ArrangementCronAdminPerformanceSectionId.DETAILED_DUPLICATE_ANALYSIS,    title: 'Detailed Duplicate Analysis' },
  { id: ArrangementCronAdminPerformanceSectionId.WORST_YEAR,                     title: 'WORST YEAR' },
  { id: ArrangementCronAdminPerformanceSectionId.BEST_YEAR,                      title: 'BEST YEAR' },
  { id: ArrangementCronAdminPerformanceSectionId.LATEST_YEAR,                   title: 'LATEST YEAR' },
];

export const ARRANGEMENT_CRON_ADMIN_PERFORMANCE_CARD_CONFIG: DashboardCardConfig<ArrangementCronAdminPerformanceSectionId>[] = [
  { id: ArrangementCronAdminPerformanceSectionId.STATS_SUMMARY_OVERVIEW,         title: 'Admin Performance Stats Summary Overview',         defaultOrder: 0,  defaultVisible: true },
  { id: ArrangementCronAdminPerformanceSectionId.TOP_PERFORMER,                  title: 'Top Performer',                                      defaultOrder: 1,  defaultVisible: true },
  { id: ArrangementCronAdminPerformanceSectionId.DUPLICATE_CRISIS,               title: 'Duplicate Crisis',                                   defaultOrder: 2,  defaultVisible: true },
  { id: ArrangementCronAdminPerformanceSectionId.AVG_RECOVERY_PER_ADMIN,         title: 'Avg. Recovery Per Admin',                            defaultOrder: 3,  defaultVisible: true },
  { id: ArrangementCronAdminPerformanceSectionId.DUPLICATE_ENTRY_TREND_OVER_TIME, title: 'Duplicate Entry Trend Over Time',                  defaultOrder: 4,  defaultVisible: true },
  { id: ArrangementCronAdminPerformanceSectionId.DUPLICATE_TREND_DOD,             title: 'Duplicate Trend DoD',                                defaultOrder: 5,  defaultVisible: true },
  { id: ArrangementCronAdminPerformanceSectionId.PERFORMANCE_DISTRIBUTION_SUCCESS, title: 'Performance Distribution - Admin Success Rate',   defaultOrder: 6,  defaultVisible: true },
  { id: ArrangementCronAdminPerformanceSectionId.RECOVERY_EFFICIENCY,            title: 'Recovery Efficiency',                                defaultOrder: 7,  defaultVisible: true },
  { id: ArrangementCronAdminPerformanceSectionId.REPEAT_ARRANGEMENT_PATTERN,      title: 'Repeat Arrangement Pattern Analysis',                defaultOrder: 14, defaultVisible: true },
  { id: ArrangementCronAdminPerformanceSectionId.ADMIN_WORKLOAD_VS_RECOVERY,    title: 'Admin Workload vs Recovery Output',                  defaultOrder: 15, defaultVisible: true },
  { id: ArrangementCronAdminPerformanceSectionId.ADMIN_PRODUCTIVITY_VS_RECOVERY,  title: 'Admin Productivity vs Recovery Yield',             defaultOrder: 16, defaultVisible: true },
  { id: ArrangementCronAdminPerformanceSectionId.DUPLICATE_ENTRIES_BY_ADMIN,     title: 'Duplicate Entries by Admin (Top 5)',               defaultOrder: 17, defaultVisible: true },
  { id: ArrangementCronAdminPerformanceSectionId.REPEAT_ARRANGEMENT_INTENSITY_DOD, title: 'Repeat Arrangement Intensity DoD',               defaultOrder: 18, defaultVisible: true },
  { id: ArrangementCronAdminPerformanceSectionId.ADMIN_DUPLICATE_EXPOSURE,       title: 'Admin Duplicate Exposure',                           defaultOrder: 19, defaultVisible: true },
  { id: ArrangementCronAdminPerformanceSectionId.DETAILED_DUPLICATE_ANALYSIS,    title: 'Detailed Duplicate Analysis',                        defaultOrder: 20, defaultVisible: true },
  { id: ArrangementCronAdminPerformanceSectionId.WORST_YEAR,                     title: 'WORST YEAR',                                         defaultOrder: 21, defaultVisible: true },
  { id: ArrangementCronAdminPerformanceSectionId.BEST_YEAR,                      title: 'BEST YEAR',                                          defaultOrder: 22, defaultVisible: true },
  { id: ArrangementCronAdminPerformanceSectionId.LATEST_YEAR,                   title: 'LATEST YEAR',                                        defaultOrder: 23, defaultVisible: true },
];

/** Maps admin chart row `id` values to backend widget keys. */
export const ARRANGEMENT_CRON_ADMIN_CHART_SECTION_ID: Readonly<
  Record<string, ArrangementCronAdminPerformanceSectionId>
> = {
  'duplicate-over-time': ArrangementCronAdminPerformanceSectionId.DUPLICATE_ENTRY_TREND_OVER_TIME,
  'duplicate-yoy': ArrangementCronAdminPerformanceSectionId.DUPLICATE_TREND_DOD,
  'performance-distribution': ArrangementCronAdminPerformanceSectionId.PERFORMANCE_DISTRIBUTION_SUCCESS,
  'recovery-efficiency': ArrangementCronAdminPerformanceSectionId.RECOVERY_EFFICIENCY,
  'repeat-pattern': ArrangementCronAdminPerformanceSectionId.REPEAT_ARRANGEMENT_PATTERN,
  'admin-workload': ArrangementCronAdminPerformanceSectionId.ADMIN_WORKLOAD_VS_RECOVERY,
  'admin-productivity': ArrangementCronAdminPerformanceSectionId.ADMIN_PRODUCTIVITY_VS_RECOVERY,
  'duplicates-by-admin': ArrangementCronAdminPerformanceSectionId.DUPLICATE_ENTRIES_BY_ADMIN,
  'repeat-intensity': ArrangementCronAdminPerformanceSectionId.REPEAT_ARRANGEMENT_INTENSITY_DOD,
};

const ARRANGEMENT_CRON_ADMIN_PERFORMANCE_KEYS = new Set<string>(
  ARRANGEMENT_CRON_ADMIN_PERFORMANCE_CARD_CONFIG.map((c) => c.id),
);
export function isArrangementCronAdminPerformanceSectionId(
  key: string,
): key is ArrangementCronAdminPerformanceSectionId {
  return ARRANGEMENT_CRON_ADMIN_PERFORMANCE_KEYS.has(key);
}

/** Denefits catalog `widget_key` → canonical section id (for embed + display-title lookup). */
export const ARRANGEMENT_CRON_ADMIN_PERFORMANCE_BACKEND_WIDGET_KEYS: Readonly<
  Partial<Record<ArrangementCronAdminPerformanceSectionId, string>>
> = {
  [ArrangementCronAdminPerformanceSectionId.STATS_SUMMARY_OVERVIEW]:         'de_ac_ap_stats_summary_overview',
  [ArrangementCronAdminPerformanceSectionId.TOP_PERFORMER]:                  'de_ac_ap_top_performer',
  [ArrangementCronAdminPerformanceSectionId.DUPLICATE_CRISIS]:               'de_ac_ap_duplicate_crisis',
  [ArrangementCronAdminPerformanceSectionId.AVG_RECOVERY_PER_ADMIN]:         'de_ac_ap_avg_recovery_per_admin',
  [ArrangementCronAdminPerformanceSectionId.DUPLICATE_ENTRY_TREND_OVER_TIME]: 'de_ac_ap_duplicate_entry_trend_over_time',
  [ArrangementCronAdminPerformanceSectionId.DUPLICATE_TREND_DOD]:             'de_ac_ap_duplicate_trend_dod',
  [ArrangementCronAdminPerformanceSectionId.PERFORMANCE_DISTRIBUTION_SUCCESS]: 'de_ac_ap_performance_distribution_admin_success_rate',
  [ArrangementCronAdminPerformanceSectionId.RECOVERY_EFFICIENCY]:            'de_ac_ap_recovery_efficiency',
  [ArrangementCronAdminPerformanceSectionId.REPEAT_ARRANGEMENT_PATTERN]:      'de_ac_ap_repeat_arrangement_pattern_analysis',
  [ArrangementCronAdminPerformanceSectionId.ADMIN_WORKLOAD_VS_RECOVERY]:    'de_ac_ap_admin_workload_vs_recovery_output',
  [ArrangementCronAdminPerformanceSectionId.ADMIN_PRODUCTIVITY_VS_RECOVERY]:  'de_ac_ap_admin_productivity_vs_recovery_yield',
  [ArrangementCronAdminPerformanceSectionId.DUPLICATE_ENTRIES_BY_ADMIN]:     'de_ac_ap_duplicate_entries_by_admin',
  [ArrangementCronAdminPerformanceSectionId.REPEAT_ARRANGEMENT_INTENSITY_DOD]: 'de_ac_ap_repeat_arrangement_intensity_dod',
  [ArrangementCronAdminPerformanceSectionId.ADMIN_DUPLICATE_EXPOSURE]:       'de_ac_ap_admin_duplicate_exposure',
  [ArrangementCronAdminPerformanceSectionId.DETAILED_DUPLICATE_ANALYSIS]:    'de_ac_ap_detailed_duplicate_analysis',
  [ArrangementCronAdminPerformanceSectionId.WORST_YEAR]:                     'de_ac_ap_worst_year',
  [ArrangementCronAdminPerformanceSectionId.BEST_YEAR]:                      'de_ac_ap_best_year',
  [ArrangementCronAdminPerformanceSectionId.LATEST_YEAR]:                   'de_ac_ap_latest_year',
};
